import {
	BarChart3,
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	ChevronUp,
	DollarSign,
	Download,
	Eye,
	Filter,
	Grid,
	Info,
	List,
	MapPin,
	Plus,
	Search,
	Target,
	TrendingUp,
	Users,
	X,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { relevanceFromDistance } from "../utils";
import { CampaignAnalysisPanel } from "./CampaignAnalysisPanel";
import { EnhancedSegmentDetailModal } from "./enhancedSegmentDetailsModal";
import { SetDetailsModal } from "./setDetailsModal";

// Download icon for export button (matching audience results)
const DownloadIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
		<polyline points="7,10 12,15 17,10"></polyline>
		<line x1="12" y1="15" x2="12" y2="3"></line>
	</svg>
);

// Chevron down icon for dropdown (matching audience results)
const ChevronDownIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<polyline points="6,9 12,15 18,9"></polyline>
	</svg>
);

// Search Icon (matching audience results)
const SearchIcon = () => (
	<svg
		width="16"
		height="16"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
	>
		<circle cx="11" cy="11" r="8"></circle>
		<path d="m21 21-4.35-4.35"></path>
	</svg>
);

// Sort Icon Component (matching audience results)
const SortIcon = ({ column, currentSortColumn, sortDirection }) => {
	const isActive = currentSortColumn === column;

	return (
		<span className="sort-icon">
			<svg
				width="12"
				height="12"
				viewBox="0 0 24 24"
				fill="none"
				stroke="currentColor"
				strokeWidth="2"
			>
				<path
					d="M7 14l5-5 5 5"
					fill={isActive && sortDirection === "asc" ? "currentColor" : "none"}
					stroke={isActive && sortDirection === "asc" ? "currentColor" : "#ccc"}
					strokeWidth={isActive && sortDirection === "asc" ? 2 : 1}
				/>
				<path
					d="M7 10l5 5 5-5"
					fill={isActive && sortDirection === "desc" ? "currentColor" : "none"}
					stroke={
						isActive && sortDirection === "desc" ? "currentColor" : "#ccc"
					}
					strokeWidth={isActive && sortDirection === "desc" ? 2 : 1}
				/>
			</svg>
		</span>
	);
};

// Helper function to build category map from categoryData
const buildCategoryDataMap = (segmentsData) => {
	const categoryMap = {};

	// Process categoryData from all search term results
	Object.values(segmentsData).forEach((searchTermData) => {
		if (
			searchTermData &&
			searchTermData.categoryData &&
			Array.isArray(searchTermData.categoryData)
		) {
			searchTermData.categoryData.forEach((categoryItem) => {
				if (categoryItem.segmentIds && categoryItem.categoryType) {
					// Split the comma-separated segment IDs and map each to the category type
					const segmentIds = categoryItem.segmentIds
						.split(",")
						.map((id) => id.trim())
						.filter((id) => id);
					segmentIds.forEach((segmentId) => {
						categoryMap[segmentId] = categoryItem.categoryType;
					});
				}
			});
		}
	});

	return categoryMap;
};

// Helper function to determine segment type - Use categoryData first, then fallback methods
const getSegmentTypeHelper = (segment, categoryDataMap = null) => {
	const segmentId = segment.segmentVector?.segmentId;

	// First method: Check categoryData mapping by segment ID (most reliable)
	if (segmentId && categoryDataMap) {
		const categoryType = categoryDataMap[segmentId];
		if (categoryType && (categoryType === "OA" || categoryType === "OAA")) {
			return categoryType;
		}
	}

	// Second method: Try to get the category directly from the backend response
	const directCategory = segment.segmentVector?.segmentCategory;
	if (directCategory) {
		const upperCategory = directCategory.toUpperCase();
		if (upperCategory.includes("OA") && !upperCategory.includes("OAA")) {
			return "OA";
		} else if (upperCategory.includes("OAA")) {
			return "OAA";
		}
	}

	// Third method: Fallback to the original logic if direct category not available
	const category = segment.segmentVector?.category;
	const subCategory = segment.segmentVector?.sub_category;

	if (category === "predefined") {
		if (
			[
				"third_party_data",
				"past_browsing_interests",
				"partner_audiences",
			].includes(subCategory)
		) {
			return "OA";
		} else if (subCategory === "bulk") {
			return "OAA";
		}
	}

	// Default to OAA if unable to determine
	return "OAA";
};

const convertToCSV = (data) => {
	if (!data || data.length === 0) return "";

	const headers = Object.keys(data[0]);
	const csvHeaders = headers.join(",");

	const csvRows = data.map((row) => {
		return headers
			.map((header) => {
				let value = row[header];

				// Handle null/undefined values
				if (value === null || value === undefined) {
					value = "";
				}

				// Convert to string and escape quotes
				value = String(value).replace(/"/g, '""');

				// Wrap in quotes if contains comma, newline, or quote
				if (
					value.includes(",") ||
					value.includes("\n") ||
					value.includes('"')
				) {
					value = `"${value}"`;
				}

				return value;
			})
			.join(",");
	});

	return [csvHeaders, ...csvRows].join("\n");
};

const downloadCSV = (csvContent, filename) => {
	const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
	const link = document.createElement("a");

	if (link.download !== undefined) {
		const url = URL.createObjectURL(blob);
		link.setAttribute("href", url);
		link.setAttribute("download", filename);
		link.style.visibility = "hidden";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	}
};

// Enhanced export function for set segments
const exportSetSegments = (setKey, setData, segmentsData, activeTab) => {
	try {
		const tabData = segmentsData[activeTab];
		if (!tabData || !tabData.data) {
			console.warn("No data available for active tab:", activeTab);
			alert("No data available for export");
			return;
		}

		const allSegments = tabData.data || [];

		// Get segment IDs that belong to this set
		let setSegmentIds = [];
		if (setData.segmentIds) {
			setSegmentIds =
				typeof setData.segmentIds === "string"
					? setData.segmentIds
							.split(",")
							.map((id) => id.trim())
							.filter((id) => id)
					: setData.segmentIds;
		}

		// Filter segments that belong to this specific set
		const setSegments = allSegments.filter((segment) => {
			const segmentId = segment.segmentVector?.segmentId || segment.id;
			return (
				setSegmentIds.includes(segmentId) ||
				setSegmentIds.includes(String(segmentId))
			);
		});

		if (setSegments.length === 0) {
			console.warn("No segments found for set:", setKey);
			alert(`No segments found for set: ${setKey}`);
			return;
		}

		// Prepare export data with detailed segment information
		const exportData = setSegments.map((segment) => {
			const segmentVector = segment.segmentVector || {};
			const segmentMetrics = segment.segmentMetrics || {};

			return {
				// Basic Segment Information
				"Segment ID": segmentVector.segmentId || "",
				"Segment Name": segmentVector.segmentName || "",
				"Full Name": segmentVector.fullName || "",
				Description: segmentVector.description || "",

				// Provider and Category Information
				"Data Provider": segmentVector.providerId || "",
				"Audience Type": segmentVector.audienceType || "",
				"Category L1": segmentVector.categoryL1 || "",
				"Category L2": segmentVector.categoryL2 || "",
				"Segment Category": segmentVector.segmentCategory || "",
				"Segment Sub Category": segmentVector.segmentSubCategory || "",
				"Segment Type": segmentVector.segmentType || "",
				"Segment Source": segmentVector.segmentSource || "",

				// Account Information
				"Account Name": segmentVector.accountName || "",
				"Provider ID": segmentVector.providerId || "",
				"External ID": segmentVector.externalId || "",

				// Audience Metrics
				"Unique Users": segmentMetrics.uniqueUsers || 0,
				"Calculated Reach": segmentMetrics.calculatedReach || 0,
				"Unique Users With Requests":
					segmentMetrics.uniqueUsersWithRequests || 0,
				"Unique Users With Impressions":
					segmentMetrics.uniqueUsersWithImpressions || 0,

				// Performance Metrics
				"Weighted Impressions": segmentMetrics.weightedImpressions || 0,
				"Weighted Clicks": segmentMetrics.weightedClicks || 0,
				"Weighted CPR Market Impressions":
					segmentMetrics.weightedCprMarketImpressions || 0,
				"Weighted Fills": segmentMetrics.weightedFills || 0,
				"Weighted Viewables": segmentMetrics.weightedViewables || 0,

				// Video Metrics
				"Weighted Start First Occurrence":
					segmentMetrics.weightedStartFirstOccurrence || 0,
				"Weighted Complete First Occurrence":
					segmentMetrics.weightedCompleteFirstOccurrence || 0,

				// Financial Metrics
				"Weighted Spend In USD": segmentMetrics.weightedSpendInUsd || 0,
				"Average eCPM": segmentMetrics.avgCpm || 0,
				"Min eCPM": segmentMetrics.minCpm || 0,
				"Max eCPM": segmentMetrics.maxCpm || 0,
				"Average Spend Per Unique User":
					segmentMetrics.avgSpendPerUniqueUser || 0,
				"Advertiser Spend": segmentMetrics.advertiserSpend || 0,

				// Device Metrics
				"Unique Connected TV Device IDs":
					segmentMetrics.uniqueConnectedTvDeviceIds || 0,
				"Unique Connected TV Device IDs With Impressions":
					segmentMetrics.uniqueConnectedTvDeviceIdsWithImpressions || 0,
				"Mobile Devices": segmentMetrics.mobileDevices || 0,
				"Mobile Devices With Impressions":
					segmentMetrics.mobileDevicesWithImpressions || 0,
				"Tablet Devices": segmentMetrics.tabletDevices || 0,
				"Tablet Devices With Impressions":
					segmentMetrics.tabletDevicesWithImpressions || 0,
				"Connected TV Devices": segmentMetrics.connectedTvDevices || 0,
				"Connected TV Devices With Impressions":
					segmentMetrics.connectedTvDevicesWithImpressions || 0,

				// Quality Metrics
				"Weighted Viewability": segmentMetrics.weightedViewability || 0,
				"Weighted Click Through Rate":
					segmentMetrics.weightedClickThroughRate || 0,
				"Weighted Video Through Rate":
					segmentMetrics.weightedVideoThroughRate || 0,
				"Impression Frequency": segmentMetrics.impressionFrequency || 0,
				"Impression Frequency Per Day":
					segmentMetrics.impressionFrequencyPerDay || 0,

				// Technical Information
				Status: segmentMetrics.latestStatus || "ready",
				"Report Months": segmentMetrics.reportMonths || "",
				Requests: segmentMetrics.requests || 0,
				"Sold Impressions": segmentMetrics.soldImpressions || 0,
				"Unique IPs With Requests": segmentMetrics.uniqueIpsWithRequests || 0,

				// Metadata
				"Is ABM": segmentVector.isAbm || false,
				"Is Leaf": segmentVector.isLeaf || false,
				"Is Archived": segmentVector.isArchived || false,
				Sellable: segmentVector.sellable || false,
				Type: segmentVector.type || "",
				"Sub Type": segmentVector.subType || "",
				Source: segmentVector.source || "",
				"Allowed Countries": Array.isArray(segmentVector.allowedCountries)
					? segmentVector.allowedCountries.join(", ")
					: "",
				"Metadata Tags": Array.isArray(segmentVector.metadata)
					? segmentVector.metadata.join(", ")
					: "",

				// Dates (formatted)
				"Date Added": segmentVector.dateAdded
					? new Date(
							segmentVector.dateAdded[0],
							segmentVector.dateAdded[1] - 1,
							segmentVector.dateAdded[2],
						)
							.toISOString()
							.split("T")[0]
					: "",
				"Expiration Date": segmentVector.expirationDate
					? new Date(
							segmentVector.expirationDate[0],
							segmentVector.expirationDate[1] - 1,
							segmentVector.expirationDate[2],
						)
							.toISOString()
							.split("T")[0]
					: "",

				// Set Information
				"Set Name": setKey,
				"Set Type": getSetDescription(setKey),
				"Export Date": new Date().toISOString().split("T")[0],
				"Export Time": new Date().toTimeString().split(" ")[0],
			};
		});

		// Generate filename
		const setDisplayName = getDisplayName(setKey);
		const timestamp = new Date()
			.toISOString()
			.replace(/[:.]/g, "-")
			.slice(0, 19);
		const filename = `${setDisplayName}_Segments_${timestamp}.csv`;

		// Convert to CSV
		const csvContent = convertToCSV(exportData);

		// Download the file
		downloadCSV(csvContent, filename);

		console.log(
			`Successfully exported ${setSegments.length} segments from set: ${setKey}`,
		);

		// Show success message
		alert(
			`Successfully exported ${setSegments.length} segments from ${setDisplayName} set`,
		);
	} catch (error) {
		console.error("Error exporting set segments:", error);
		alert("Error exporting set segments. Please try again.");
	}
};

const getDisplayName = (key) => {
	const displayMap = {
		Perfomance: "Performance Optimized",
		Performance: "Performance Optimized",
		Technology: "Relevance Focused",
		Automotive: "Direct Publishers",
		Travel: "Travel Focused",
		Fashion: "Fashion Focused",
	};

	if (key.includes("_")) {
		const parts = key.split("_");
		if (parts.length === 2) {
			const [searchTerm, setType] = parts;
			const capitalizedSearchTerm =
				searchTerm.charAt(0).toUpperCase() + searchTerm.slice(1);
			const setTypeDisplay =
				setType === "Perfomance" ? "Performance Optimized" : setType;
			return `${capitalizedSearchTerm} ${setTypeDisplay}`;
		}
	}

	return displayMap[key] || key.replace(/([A-Z])/g, " $1").trim();
};

const getSetDescription = (key) => {
	const descriptions = {
		Perfomance: "Performance Optimized",
		Performance: "Performance Optimized",
		Technology: "Relevance Focused",
		Automotive: "Direct Publishers",
		Travel: "Travel Focused",
		Fashion: "Fashion Focused",
	};

	if (key.includes("_")) {
		const parts = key.split("_");
		if (parts.length === 2) {
			const [searchTerm] = parts;
			return `${searchTerm.charAt(0).toUpperCase() + searchTerm.slice(1)} Optimized`;
		}
	}

	return descriptions[key] || "Optimized Domain Set";
};

// Set View Component - Shows cumulative data for each search term (removed bulk actions)
const EnhancedSetView = ({
	cumulativeData = {},
	selectedSets = [],
	onSetSelect,
	onSetAdd,
	showSetDetails,
	activeTab,
	segmentsData = {},
	onExportSet,
}) => {
	const formatNumber = (num) => {
		if (!num) return "0";
		if (num >= 1000000000) return `${(num / 1000000000).toFixed(1)}B`;
		if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
		if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
		return num.toLocaleString();
	};

	const getDisplayName = (key) => {
		const displayMap = {
			Perfomance: "Performance Optimized",
			Performance: "Performance Optimized",
			Technology: "Relevance Focused",
			Automotive: "Direct Publishers",
			Travel: "Travel Focused",
			Fashion: "Fashion Focused",
		};

		if (key.includes("_")) {
			const parts = key.split("_");
			if (parts.length === 2) {
				const [searchTerm, setType] = parts;
				const capitalizedSearchTerm =
					searchTerm.charAt(0).toUpperCase() + searchTerm.slice(1);
				const setTypeDisplay =
					setType === "Perfomance" ? "Performance Optimized" : setType;
				return `${capitalizedSearchTerm} ${setTypeDisplay}`;
			}
		}

		return displayMap[key] || key.replace(/([A-Z])/g, " $1").trim();
	};

	const getCorrespondingMetricName = (optimizationType) => {
		// Remove "Optimised" from the end and clean up the string
		const metricName = optimizationType
			.replace(/\s*Optimised?$/i, "") // Remove "Optimised" or "Optimized" (case insensitive)
			.replace(/([A-Z])/g, " $1") // Add space before capital letters
			.trim(); // Remove leading/trailing spaces

		// Handle special cases
		const specialCases = {
			"Unique Users WIth Impressions": "Unique Users",
			"Unique User": "Unique Users",
			Requests: "Requests",
			Impressions: "Impressions",
		};

		return specialCases[metricName] || metricName;
	};

	// Calculate relevance score using the correct formula from segment 'score' field
	const calculateRelevanceScore = (setKey, setData) => {
		const tabData = segmentsData[activeTab];
		if (!tabData || !tabData.data) return 75;

		const allSegments = tabData.data || [];

		// Get segment IDs for this set from setData.segmentIds array
		const setSegmentIds = setData.segmentIds || [];

		// Filter segments that belong to this specific set
		const setSegments = allSegments.filter((segment) => {
			const segmentId = segment.segmentVector?.segmentId;
			return segmentId && setSegmentIds.includes(segmentId);
		});

		// If no segments match by ID, fall back to string comparison
		if (setSegments.length === 0) {
			const setSegmentsStr = allSegments.filter((segment) => {
				const segmentId = String(segment.segmentVector?.segmentId || "");
				return segmentId && setSegmentIds.map(String).includes(segmentId);
			});

			if (setSegmentsStr.length > 0) {
				const individualRelevanceScores = [];

				setSegmentsStr.forEach((segment) => {
					const domainScore = segment.score; // Use 'score' field, not compositeScore
					if (domainScore !== undefined && domainScore !== null) {
						const score = parseFloat(domainScore);
						individualRelevanceScores.push(
							relevanceFromDistance(score, "SEGMENTS"),
						);
					}
				});

				if (individualRelevanceScores.length === 0) return 75;

				const avgRelevanceScore =
					individualRelevanceScores.reduce((sum, score) => sum + score, 0) /
					individualRelevanceScores.length;

				return Math.round(avgRelevanceScore);
			}
		}

		// Calculate relevance score using the correct formula for segments in this set
		const individualRelevanceScores = [];

		setSegments.forEach((segment) => {
			const domainScore = segment.score; // Use 'score' field, not compositeScore
			if (domainScore !== undefined && domainScore !== null) {
				const score = parseFloat(domainScore);
				individualRelevanceScores.push(
					relevanceFromDistance(score, "SEGMENTS"),
				);
			}
		});

		if (individualRelevanceScores.length === 0) return 75;

		// Calculate average relevance score for this set
		const avgRelevanceScore =
			individualRelevanceScores.reduce((sum, score) => sum + score, 0) /
			individualRelevanceScores.length;

		return Math.round(avgRelevanceScore);
	};

	// Calculate aggregated metrics from segments data for each specific set (RESTORED to working version)
	const calculateSetMetrics = (setKey, setData) => {
		const tabData = segmentsData[activeTab];
		if (!tabData || !tabData.data) {
			return {
				totalImpressions: 0,
				segments: setData.noOfSegments || 0,
				correspondingMetricName: getCorrespondingMetricName(setKey),
				correspondingMetric: setData.reach || 0,
				reach: 0,
				clickRate: 0,
				viewability: 0,
			};
		}

		const allSegments = tabData.data || [];

		// Get segment IDs that belong to this set
		let setSegmentIds = [];
		if (setData.segmentIds) {
			// If segmentIds is a string, split it; if it's an array, use it directly
			setSegmentIds =
				typeof setData.segmentIds === "string"
					? setData.segmentIds
							.split(",")
							.map((id) => id.trim())
							.filter((id) => id)
					: setData.segmentIds;
		}

		// Filter segments that belong to this specific set based on segment IDs
		const setSegments = allSegments.filter((segment) => {
			const segmentId = segment.segmentVector?.segmentId || segment.id;
			return (
				setSegmentIds.includes(segmentId) ||
				setSegmentIds.includes(String(segmentId))
			);
		});

		// If no segments found by ID matching, fall back to proportional distribution
		// This handles cases where the ID matching might not work perfectly
		let finalSetSegments = setSegments;
		if (setSegments.length === 0 && setData.noOfSegments > 0) {
			const setIndex = Object.keys(cumulativeData).indexOf(setKey);
			const segmentsPerSet = Math.ceil(
				allSegments.length / Object.keys(cumulativeData).length,
			);
			finalSetSegments = allSegments.slice(
				setIndex * segmentsPerSet,
				Math.min((setIndex + 1) * segmentsPerSet, allSegments.length),
			);
		}

		// Aggregate metrics only from segments that belong to this set
		const metrics = finalSetSegments.reduce(
			(acc, segment) => {
				const segmentMetrics = segment.segmentMetrics || {};

				// Total impressions
				acc.totalImpressions += segmentMetrics.weightedImpressions || 0;

				// Total clicks for click rate calculation
				acc.totalClicks += segmentMetrics.weightedClicks || 0;

				// Total reach (unique users)
				acc.totalReach += segmentMetrics.calculatedReach || 0;

				// Total viewable impressions for viewability calculation
				acc.totalViewables += segmentMetrics.weightedViewables || 0;

				acc.totalAvgEcpm += segmentMetrics.avgCpm || 0;

				if (
					segmentMetrics.weightedViewability !== undefined &&
					segmentMetrics.weightedViewability !== null
				) {
					acc.viewabilityValues.push(segmentMetrics.weightedViewability);
				}

				return acc;
			},
			{
				totalImpressions: 0,
				totalClicks: 0,
				totalReach: 0,
				totalAvgEcpm: 0,
				viewabilityValues: [],
			},
		);

		// Calculate derived metrics
		const clickRate =
			metrics.totalImpressions > 0
				? (metrics.totalClicks / metrics.totalImpressions) * 100
				: 0;

		const viewability =
			metrics.viewabilityValues.length > 0
				? metrics.viewabilityValues.reduce((sum, val) => sum + val, 0) /
					metrics.viewabilityValues.length
				: 0;

		return {
			totalImpressions: metrics.totalImpressions,
			segments: setData.noOfSegments || finalSetSegments.length,
			correspondingMetricName: getCorrespondingMetricName(setKey),
			correspondingMetric: setData.reach || 0, // Keep original corresponding metric value
			reach: metrics.totalReach,
			clickRate: clickRate,
			viewability: viewability,
			avgEcpm: metrics.totalAvgEcpm,
		};
	};

	const getTabSpecificSetKey = (setKey) => {
		return `${activeTab}_${setKey}`;
	};

	const isSetSelected = (setKey) => {
		const tabSpecificKey = getTabSpecificSetKey(setKey);
		return selectedSets.includes(tabSpecificKey);
	};

	const handleSetSelect = (setKey) => {
		const tabSpecificKey = getTabSpecificSetKey(setKey);
		onSetSelect && onSetSelect(tabSpecificKey);
	};

	const handleExportSet = (setKey, setData) => {
		exportSetSegments(setKey, setData, segmentsData, activeTab);
	};

	if (!cumulativeData || Object.keys(cumulativeData).length === 0) {
		return null;
	}

	// Convert object to array and ensure we have exactly 4 items (or pad if needed)
	const setsArray = Object.entries(cumulativeData);
	while (setsArray.length < 4) {
		setsArray.push([
			`placeholder_${setsArray.length}`,
			{ noOfSegments: 0, reach: 0, isPlaceholder: true },
		]);
	}

	return (
		<div className="enhanced-set-view-container">
			<div className="set-view-header">
				<h3>Available Performance Sets</h3>
				<p className="set-view-description">
					Select entire performance sets or individual segments from tabs below
				</p>
			</div>

			<div className="enhanced-sets-grid">
				{setsArray.slice(0, 4).map(([setKey, setData]) => {
					if (setData.isPlaceholder) {
						return (
							<div key={setKey} className="set-card placeholder-card">
								<div className="placeholder-content">
									<div className="placeholder-text">Set Placeholder</div>
								</div>
							</div>
						);
					}

					const relevanceScore = calculateRelevanceScore(setKey, setData);
					const metrics = calculateSetMetrics(setKey, setData);

					return (
						<div key={setKey} className="enhanced-set-card">
							<div className="set-card-header">
								<div
									className="set-checkbox"
									onClick={(e) => e.stopPropagation()}
								>
									<input
										type="checkbox"
										checked={isSetSelected(setKey)}
										onChange={() => handleSetSelect(setKey)}
									/>
								</div>
								<div className="relevance-circle" data-score={relevanceScore}>
									<div className="circle-content">
										<span className="score">{relevanceScore}</span>
									</div>
								</div>
							</div>

							<div
								className="set-card-content"
								onClick={() =>
									showSetDetails && showSetDetails(setKey, setData)
								}
							>
								<h4 className="set-name">{getDisplayName(setKey)}</h4>
								<p className="set-description">{getSetDescription(setKey)}</p>

								{/* Row 1: Segments, Corresponding Metric, Reach */}
								<div className="primary-metrics-row">
									<div className="metric-group">
										<div className="metric-label">SEGMENTS</div>
										<div className="metric-value">{metrics.segments}</div>
									</div>
									<div className="metric-group">
										<div className="metric-label">TOTAL IMPRESSIONS</div>
										<div className="metric-value">
											{metrics.totalImpressions > 0
												? formatNumber(metrics.totalImpressions)
												: "N/A"}
										</div>
									</div>
									<div className="metric-group">
										<div className="metric-label">REACH</div>
										<div className="metric-value">
											{formatNumber(metrics.reach)}
										</div>
									</div>
								</div>

								{/* Row 2: Click Rate, Total Impressions, Viewability */}
								<div className="secondary-metrics-row">
									<div className="metric-group">
										<div className="metric-label">CLICK RATE</div>
										<div className="metric-value">
											{metrics.clickRate > 0
												? `${metrics.clickRate.toFixed(3)}%`
												: "N/A"}
										</div>
									</div>
									<div className="metric-group">
										<div className="metric-label">Avg. ECPM</div>
										<div className="metric-value">
											$
											{metrics.segments > 0
												? (metrics.avgEcpm / metrics.segments).toFixed(3)
												: "N/A"}
										</div>
									</div>
									<div className="metric-group">
										<div className="metric-label">VIEWABILITY</div>
										<div className="metric-value">
											{metrics.viewability > 0
												? `${metrics.viewability.toFixed(1)}%`
												: "N/A"}
										</div>
									</div>
								</div>
							</div>

							{/* Action Buttons */}
							<div className="set-card-actions">
								<button
									className="action-btn view-details"
									onClick={() =>
										showSetDetails && showSetDetails(setKey, setData)
									}
								>
									View Details →
								</button>
								<button
									className="btn-medium-secondary"
									onClick={(e) => {
										e.stopPropagation();
										handleExportSet(setKey, setData);
									}}
								>
									Export
								</button>
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};

const EnhancedSegmentsExportDropdown = ({
	isExporting,
	onExport,
	currentTabData = [],
	totalTabsData = {},
	selectedCount = 0,
	activeTab = "",
}) => {
	const [isOpen, setIsOpen] = useState(false);

	const getTotalSegmentsCount = () => {
		return Object.entries(totalTabsData)
			.filter(([key]) => !key.startsWith("Demographic_"))
			.reduce((total, [key, data]) => total + (data?.data?.length || 0), 0);
	};

	const totalCount = getTotalSegmentsCount();

	return (
		<div className="export-dropdown" style={{ position: "relative" }}>
			<button
				onClick={() => setIsOpen(!isOpen)}
				className="btn-medium-secondary"
				disabled={isExporting || totalCount === 0}
				style={{
					display: "flex",
					alignItems: "center",
					gap: "8px",
				}}
			>
				<DownloadIcon />
				{isExporting ? "Exporting..." : "Export Results"}
				<ChevronDownIcon />
			</button>

			{isOpen && (
				<div
					className="dropdown-menu"
					style={{
						position: "absolute",
						top: "100%",
						left: 0,
						backgroundColor: "white",
						border: "1px solid #dee2e6",
						borderRadius: "4px",
						boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
						zIndex: 1000,
						minWidth: "250px",
						marginTop: "4px",
					}}
				>
					{/* Current Tab Export */}
					<div
						className="dropdown-section"
						style={{
							borderBottom: "1px solid #e9ecef",
							padding: "4px 0",
						}}
					>
						<button
							onClick={() => {
								onExport("summarized", "current");
								setIsOpen(false);
							}}
							className="dropdown-item"
							onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
							onMouseLeave={(e) =>
								(e.target.style.backgroundColor = "transparent")
							}
						>
							<div>
								<div style={{ fontWeight: "500" }}>
									Export Current Tab - Summarized
								</div>
								<div style={{ fontSize: "11px", color: "#6c757d" }}>
									{currentTabData.length} segments from {activeTab}
								</div>
							</div>
						</button>

						<button
							onClick={() => {
								onExport("detailed", "current");
								setIsOpen(false);
							}}
							className="dropdown-item"
							onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
							onMouseLeave={(e) =>
								(e.target.style.backgroundColor = "transparent")
							}
						>
							<div>
								<div style={{ fontWeight: "500" }}>
									Export Current Tab - Detailed
								</div>
								<div style={{ fontSize: "11px", color: "#6c757d" }}>
									All metrics from {activeTab} tab
								</div>
							</div>
						</button>
					</div>

					{/* All Tabs Export */}
					<div
						className="dropdown-section"
						style={{
							borderBottom: selectedCount > 0 ? "1px solid #e9ecef" : "none",
							padding: "4px 0",
						}}
					>
						<button
							onClick={() => {
								onExport("summarized", "all");
								setIsOpen(false);
							}}
							className="dropdown-item"
							onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
							onMouseLeave={(e) =>
								(e.target.style.backgroundColor = "transparent")
							}
						>
							<div>
								<div style={{ fontWeight: "500" }}>
									Export All Tabs - Summarized
								</div>
								<div style={{ fontSize: "11px", color: "#6c757d" }}>
									{totalCount} segments from all search results
								</div>
							</div>
						</button>

						<button
							onClick={() => {
								onExport("detailed", "all");
								setIsOpen(false);
							}}
							className="dropdown-item"
							onMouseEnter={(e) => (e.target.style.backgroundColor = "#f8f9fa")}
							onMouseLeave={(e) =>
								(e.target.style.backgroundColor = "transparent")
							}
						>
							<div>
								<div style={{ fontWeight: "500" }}>
									Export All Tabs - Comprehensive
								</div>
								<div style={{ fontSize: "11px", color: "#6c757d" }}>
									Complete dataset with source tab info
								</div>
							</div>
						</button>
					</div>

					{/* Selected Only Export */}
					{selectedCount > 0 && (
						<div className="dropdown-section" style={{ padding: "4px 0" }}>
							<div
								style={{
									padding: "4px 12px",
									fontSize: "11px",
									fontWeight: "600",
									color: "#6c757d",
									textTransform: "uppercase",
								}}
							>
								Selected Only
							</div>

							<button
								onClick={() => {
									onExport("summarized", "selected");
									setIsOpen(false);
								}}
								className="dropdown-item"
								onMouseEnter={(e) =>
									(e.target.style.backgroundColor = "#f8f9fa")
								}
								onMouseLeave={(e) =>
									(e.target.style.backgroundColor = "transparent")
								}
							>
								<div>
									<div style={{ fontWeight: "500" }}>
										Export Selected - Summarized
									</div>
									<div style={{ fontSize: "11px", color: "#6c757d" }}>
										{selectedCount} selected segments only
									</div>
								</div>
							</button>

							<button
								onClick={() => {
									onExport("detailed", "selected");
									setIsOpen(false);
								}}
								className="dropdown-item"
								onMouseEnter={(e) =>
									(e.target.style.backgroundColor = "#f8f9fa")
								}
								onMouseLeave={(e) =>
									(e.target.style.backgroundColor = "transparent")
								}
							>
								<div>
									<div style={{ fontWeight: "500" }}>
										Export Selected - Comprehensive
									</div>
									<div style={{ fontSize: "11px", color: "#6c757d" }}>
										Complete data for selected segments
									</div>
								</div>
							</button>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

// Simple Pagination Component (matching audience results style)
const Pagination = (props) => {
	const {
		currentPage,
		totalPages,
		totalItems,
		itemsPerPage,
		onPageChange,
		onPageSizeChange,
		pageSizeOptions = [25, 50, 100],
		loading = false,
		showJumpToPage = true,
		showPageSizeSelector = true,
		showResultsInfo = true,
		forceShow = false,
	} = props;

	if (totalPages <= 1 && !forceShow) return null;

	return (
		<div className="pagination-container">
			{showResultsInfo && (
				<div className="pagination-info">
					Showing {(currentPage - 1) * itemsPerPage + 1} -{" "}
					{Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
					results
				</div>
			)}

			<div className="pagination-controls">
				<button
					onClick={() => onPageChange(currentPage - 1)}
					disabled={currentPage === 1 || loading}
					className="pagination-btn"
				>
					<ChevronLeft className="w-4 h-4" />
				</button>

				<span className="pagination-current">
					Page {currentPage} of {totalPages}
				</span>

				<button
					onClick={() => onPageChange(currentPage + 1)}
					disabled={currentPage === totalPages || loading}
					className="pagination-btn"
				>
					<ChevronRight className="w-4 h-4" />
				</button>
			</div>

			{showPageSizeSelector && (
				<div className="page-size">
					<select
						value={itemsPerPage}
						onChange={(e) => onPageSizeChange(parseInt(e.target.value))}
						className="page-size-select"
					>
						{pageSizeOptions.map((size) => (
							<option key={size} value={size}>
								{size} per page
							</option>
						))}
					</select>
				</div>
			)}
		</div>
	);
};

const SORT_DISPLAY_NAMES = {
	name: "Name", segmentName: "Name",
	audienceType: "Audience Type",
	provider: "Provider",
	category: "Category",
	compositeScore: "Composite Score",
	highestScaleScore: "Highest Scale",
	bestPerformanceScore: "Best Perf.",
	mostRelevantScore: "Most Relevant",
	searchScore: "Relevance",
	reach: "Reach", calculatedReach: "Reach",
	uniqueUsers: "Unique Users",
	weightedImpressions: "Impressions",
	ecpm: "eCPM", avgEcpm: "eCPM",
	status: "Status",
	reportMonths: "Report Months",
	description: "Description",
	weightedClicks: "Clicks",
	weightedFills: "Fills",
	activeUsers: "Active Users",
	activeIps: "Active IPs",
	activeUsersWithImpressions: "Active Users with Impressions",
	videoStarts: "Video Starts",
	videoCompletions: "Video Completions",
	weightedViewables: "Viewable Impressions",
	weightedSpendInUsd: "Spend ($)",
	avgSpendPerUniqueUser: "Avg Spend Per User",
};

const STRING_SORT_KEYS = new Set([
	"name", "segmentName", "audienceType", "provider",
	"category", "status", "reportMonths", "description",
	"primaryPerson", "strategy",
]);

const SCORING_TABS = [
	{ key: "highestScale", label: "Highest Scale", color: "#0076bd" },
	{ key: "bestPerformance", label: "Best Performance", color: "#00875a" },
	{ key: "mostRelevant", label: "Most Relevant", color: "#6b4fbb" },
];

// Main Component - Table view only with Set View above
export const SegmentsResultsList = (props) => {
	const {
		segmentsData: segmentsDataProp = {},
		demographicData = {},
		cumulativeData = {}, // Legacy prop - kept for backward compatibility
		segments = [],
		loading = false,
		error = null,
		onSegmentSelect,
		onSegmentView,
		onSegmentAdd,
		onSetSelect, // NEW: Set selection handler
		onSetAdd, // NEW: Set add handler
		selectedSegments = [],
		selectedSets = [], // NEW: Selected sets
		onSelectAll,
		isAllSelected = false,
		isExporting = false,
		onExport,
		logicChain = [], // NEW: Accept logic chain from search interface
		searchTerms = [],
		currentFilters = {},
		onExportSet,
		showMultiScores = false,
		onTotalCountChange,
		multiScoredData = null,
	} = props;

	// State
	const [activeTab, setActiveTab] = useState("");
	const [activeScoringTab, setActiveScoringTab] = useState("highestScale");
	const [searchTerm, setSearchTerm] = useState("");
	const [sortBy, setSortBy] = useState("compositeScore");
	const [sortOrder, setSortOrder] = useState("desc");
	const [currentPage, setCurrentPage] = useState(1);
	const [pageSize, setPageSize] = useState(25);
	const [selectedSegment, setSelectedSegment] = useState(null);
	const [showModal, setShowModal] = useState(false);
	const [isSearchVisible, setIsSearchVisible] = useState(false);
	const [showSetDetails, setShowSetDetails] = useState(false);
	const [selectedSetForModal, setSelectedSetForModal] = useState(null);
	const [selectedSetData, setSelectedSetData] = useState(null);
	const [categoryFilter, setCategoryFilter] = useState("ALL"); // ALL shows all, OA shows only OA, OAA shows only OAA
	const [showCampaignPanel, setShowCampaignPanel] = useState(false);
	const [hoveredSegment, setHoveredSegment] = useState(null);
	const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

	// When multiScoredData is provided, derive segmentsData from the active scoring tab
	const segmentsData = useMemo(
		() => multiScoredData ? (multiScoredData[activeScoringTab]?.data || {}) : segmentsDataProp,
		[multiScoredData, activeScoringTab, segmentsDataProp]
	);

	// Ensure API service is available
	useEffect(() => {
		// Check if the segments API service is loaded
		if (!window.segmentsApiService) {
			console.warn("⚠️ SegmentsApiService not found. Make sure it is loaded.");
		} else {
			console.log(
				"✅ SegmentsApiService is available for audience data fetching",
			);
		}
	}, []);

	// Clear selected segments when segmentsData changes (new search performed)
	useEffect(() => {
		if (onSegmentSelect && selectedSegments.length > 0) {
			// Get all current segment IDs from all tabs
			const currentSegmentIds = new Set();
			Object.entries(segmentsData)
				.filter(([key]) => !key.startsWith("Demographic_"))
				.forEach(([key, data]) => {
					if (data && Array.isArray(data.data)) {
						data.data.forEach((segment) => {
							const segmentId = segment.segmentVector?.segmentId || segment.id;
							if (segmentId) {
								currentSegmentIds.add(segmentId);
							}
						});
					}
				});

			// Find selected segments that no longer exist in current data
			const invalidSelections = selectedSegments.filter(
				(segmentId) => !currentSegmentIds.has(segmentId),
			);

			// Clear invalid selections
			if (invalidSelections.length > 0) {
				console.log("Clearing invalid segment selections:", invalidSelections);
				invalidSelections.forEach((segmentId) => {
					onSegmentSelect(segmentId); // This should toggle/remove the selection
				});
			}
		}
	}, [segmentsData, selectedSegments, onSegmentSelect]); // Only trigger when segmentsData changes

	// Extract cumulative data for the active tab only
	const extractedCumulativeData = useMemo(() => {
		const extracted = {};

		// Check if we have the legacy cumulativeData prop first
		if (cumulativeData && Object.keys(cumulativeData).length > 0) {
			console.log("📊 Using legacy cumulativeData prop:", cumulativeData);
			return cumulativeData;
		}

		// If no active tab selected yet, return empty
		if (!activeTab) {
			return extracted;
		}

		// Extract cumulative data only for the active tab
		const activeTabData = segmentsData[activeTab];
		if (activeTabData && activeTabData.cumulativeData) {
			// Use the cumulative data from the active tab directly
			Object.entries(activeTabData.cumulativeData).forEach(
				([setKey, setData]) => {
					extracted[setKey] = {
						...setData,
						searchTerm: activeTab, // Add reference to which search term this came from
						originalSetKey: setKey, // Keep original key
					};
				},
			);
		}

		return extracted;
	}, [segmentsData, cumulativeData, activeTab]);

	// Build category data map from all search terms for accurate OA/OAA detection
	const categoryDataMap = useMemo(() => {
		return buildCategoryDataMap(segmentsData);
	}, [segmentsData]);

	const demographicProbabilities = useMemo(() => {
		const ageProbability =
			segmentsData["Demographic_Age"]?.demographicProbabilities?.[
				"Demographic_Age"
			] || 1;
		const locationProbability =
			segmentsData["Demographic_Location"]?.demographicProbabilities?.[
				"Demographic_Location"
			] || 1;
		const genderProbability =
			segmentsData["Demographic_Gender"]?.demographicProbabilities?.[
				"Demographic_Gender"
			] || 1;

		return {
			age: ageProbability,
			location: locationProbability,
			gender: genderProbability,
		};
	}, [segmentsData]);

	// Helper functions
	const getDisplayName = (key) => {
		const displayMap = {
			Demographic_Location: "Geographic Location",
			Demographic_Age: "Age Demographics",
			Demographic_Gender: "Gender Demographics",
			SearchTerm_Cars: "Automotive Interests",
			SearchTerm_Travel: "Travel Interests",
			SearchTerm_Fashion: "Fashion Interests",
			SearchTerm_Technology: "Technology Interests",
			sports_Perfomance: "Sports Performance Set", // Handle nested structure
			cars_Performance: "Automotive Performance Set",
			travel_Performance: "Travel Performance Set",
			Perfomance: "Performance Set",
			Performance: "Performance Set",
		};

		if (displayMap[key]) return displayMap[key];

		// Handle prefixed set keys (e.g., "sports_Perfomance")
		if (key.includes("_")) {
			const parts = key.split("_");
			if (parts.length === 2) {
				const [searchTerm, setType] = parts;
				const capitalizedSearchTerm =
					searchTerm.charAt(0).toUpperCase() + searchTerm.slice(1);
				const setTypeDisplay =
					setType === "Perfomance" ? "Performance" : setType;
				return `${capitalizedSearchTerm} ${setTypeDisplay} Set`;
			}
		}

		if (key.startsWith("SearchTerm_")) {
			const term = key.replace("SearchTerm_", "");
			return `${term.charAt(0).toUpperCase() + term.slice(1)}`;
		}

		if (key.startsWith("Demographic_")) {
			const term = key.replace("Demographic_", "");
			return `${term.charAt(0).toUpperCase() + term.slice(1)} Demographics`;
		}

		return key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
	};

	// Calculate demographic-filtered effective reach
	const calculateDemographicFilteredReach = (effectiveReach) => {
		if (!segmentsData || effectiveReach === 0) return effectiveReach;

		// Debug: Show available demographic keys
		const demographicKeys = Object.keys(segmentsData).filter((key) =>
			key.startsWith("Demographic_"),
		);

		// Get demographic probabilities from the main segmentsData, default to 1 if not available
		const ageProbability =
			segmentsData["Demographic_Age"]?.demographicProbabilities?.[
				"Demographic_Age"
			] || 1;
		const locationProbability =
			segmentsData["Demographic_Location"]?.demographicProbabilities?.[
				"Demographic_Location"
			] || 1;
		const genderProbability =
			segmentsData["Demographic_Gender"]?.demographicProbabilities?.[
				"Demographic_Gender"
			] || 1;

		const filteredReach =
			effectiveReach * ageProbability * locationProbability * genderProbability;

		return filteredReach;
	};

	const getHiddenSelectionsCount = () => {
		const visibleSegmentIds = filteredAndSortedData.map(
			(segment) => segment.id,
		);
		const hiddenSelections = selectedSegments.filter(
			(id) => !visibleSegmentIds.includes(id),
		);
		return hiddenSelections.length;
	};

	const formatNumber = (num) => {
		if (!num) return "0";
		return num.toLocaleString();
	};

	const getCompositeScoreClass = (score) => {
		if (score >= 80) return "excellent";
		if (score >= 70) return "great";
		if (score >= 60) return "acceptable";
		if (score >= 50) return "poor";
		return "irrelevant";
	};

	const getStatusClass = (status) => {
		switch (status?.toUpperCase()) {
			case "READY":
				return "status-success";
			case "ERROR":
				return "status-error";
			default:
				return "status-unknown";
		}
	};

	const handleSegmentNameHover = useCallback((segment, event) => {
		const rect = event.currentTarget.getBoundingClientRect();
		setTooltipPosition({
			x: rect.left + rect.width / 2,
			y: rect.top - 50,
		});
		setHoveredSegment(segment);
	}, []);

	const handleSegmentNameLeave = useCallback(() => {
		setHoveredSegment(null);
	}, []);

	// Calculate total effective reach
	const totalEffectiveReach = useMemo(() => {
		if (!segmentsData || Object.keys(segmentsData).length === 0) return 0;

		return Object.entries(segmentsData)
			.filter(([key]) => !key.startsWith("Demographic_"))
			.reduce((total, [key, data]) => {
				const effectiveReach = data?.orfData?.effectiveReach || 0;
				return total + effectiveReach;
			}, 0);
	}, [segmentsData]);

	// Generate available tabs - Show all non-demographic results with effective reach
	const availableTabs = useMemo(() => {
		if (Object.keys(segmentsData).length > 0) {
			return Object.entries(segmentsData)
				.filter(([key, data]) => {
					// Hide only Demographic_ results, show everything else
					const isDemographic = key.startsWith("Demographic_");
					const hasData =
						data && Array.isArray(data.data) && data.data.length > 0;
					return !isDemographic && hasData;
				})
				.map(([key, data]) => {
					const effectiveReach = data.orfData?.effectiveReach || 0;
					const demographicFilteredReach =
						calculateDemographicFilteredReach(effectiveReach);
					return {
						key,
						name: getDisplayName(key),
						count: data.data.length,
						effectiveReach,
						demographicFilteredReach,
					};
				});
		}

		if (segments.length > 0) {
			return [
				{
					key: "all",
					name: "All Segments",
					count: segments.length,
					effectiveReach: 0,
					demographicFilteredReach: 0,
				},
			];
		}

		return [];
	}, [segmentsData, segments, demographicData]);

	// Initialize first tab
	useEffect(() => {
		if (availableTabs.length > 0) {
			// If no active tab or current active tab doesn't exist in available tabs, set to first available
			if (!activeTab || !availableTabs.some((tab) => tab.key === activeTab)) {
				console.log("Setting active tab to:", availableTabs[0].key);
				setActiveTab(availableTabs[0].key);
			}
		} else {
			// No available tabs, clear active tab
			if (activeTab) {
				console.log("Clearing active tab - no available tabs");
				setActiveTab("");
			}
		}
	}, [availableTabs]);

	useEffect(() => {
		if (!onTotalCountChange) return;
		const total = availableTabs.reduce(
			(sum, tab) => sum + (segmentsData[tab.key]?.data?.length ?? 0),
			0
		);
		onTotalCountChange(total);
	}, [availableTabs, segmentsData, onTotalCountChange]);

	// Get current tab data
	const currentTabData = useMemo(() => {
		if (Object.keys(segmentsData).length > 0) {
			const tabData = segmentsData[activeTab];
			if (!tabData || !Array.isArray(tabData.data)) {
				return [];
			}
			return tabData.data.map((item, index) => ({
				...item,
				id: item.segmentVector?.segmentId || `${activeTab}-${index}`,
			}));
		}

		return segments.map((item, index) => ({
			...item,
			id: item.segmentVector?.segmentId || item.id || index,
		}));
	}, [segmentsData, segments, activeTab]);

	// Filter and sort data
	const filteredAndSortedData = useMemo(() => {
		let filtered = currentTabData;

		// Apply search filter
		if (searchTerm) {
			filtered = filtered.filter(
				(segment) =>
					segment.segmentVector?.segmentName
						?.toLowerCase()
						.includes(searchTerm.toLowerCase()) ||
					segment.segmentVector?.description
						?.toLowerCase()
						.includes(searchTerm.toLowerCase()) ||
					segment.segmentVector?.segmentCategory
						?.toLowerCase()
						.includes(searchTerm.toLowerCase()),
			);
		}

		// Apply category filter
		if (categoryFilter === "OA") {
			filtered = filtered.filter((segment) => {
				const segmentType = getSegmentTypeHelper(segment, categoryDataMap);
				return segmentType === "OA";
			});
		} else if (categoryFilter === "OAA") {
			filtered = filtered.filter((segment) => {
				const segmentType = getSegmentTypeHelper(segment, categoryDataMap);
				return segmentType === "OAA";
			});
		}
		// ALL shows all segments (no additional filtering needed)

		// Apply sorting
		filtered.sort((a, b) => {
			let aVal, bVal;

			switch (sortBy) {
				case "name":
				case "segmentName":
					aVal = a.segmentVector?.segmentName || "";
					bVal = b.segmentVector?.segmentName || "";
					break;
				case "audienceType":
					aVal = a.segmentVector?.audienceType || a.audienceType || "";
					bVal = b.segmentVector?.audienceType || b.audienceType || "";
					break;
				case "provider":
					aVal = a.segmentVector?.provider || a.provider || "";
					bVal = b.segmentVector?.provider || b.provider || "";
					break;
				case "category":
					aVal = getSegmentTypeHelper(a, categoryDataMap) || "ZZZ"; // Put null values at end
					bVal = getSegmentTypeHelper(b, categoryDataMap) || "ZZZ";
					break;
				case "compositeScore":
					aVal = a.compositeScore || 0;
					bVal = b.compositeScore || 0;
					break;
				case "highestScaleScore":
					aVal = a.highestScaleScore || 0;
					bVal = b.highestScaleScore || 0;
					break;
				case "bestPerformanceScore":
					aVal = a.bestPerformanceScore || 0;
					bVal = b.bestPerformanceScore || 0;
					break;
				case "mostRelevantScore":
					aVal = a.mostRelevantScore || 0;
					bVal = b.mostRelevantScore || 0;
					break;
				case "searchScore":
					aVal = a.score || 0;
					bVal = b.score || 0;
					break;
				case "reach":
				case "calculatedReach":
					aVal = a.segmentMetrics?.calculatedReach || 0;
					bVal = b.segmentMetrics?.calculatedReach || 0;
					break;
				case "uniqueUsers":
					aVal = a.segmentMetrics?.uniqueUsers || 0;
					bVal = b.segmentMetrics?.uniqueUsers || 0;
					break;
				case "weightedImpressions":
					aVal = a.segmentMetrics?.weightedImpressions || 0;
					bVal = b.segmentMetrics?.weightedImpressions || 0;
					break;
				case "ecpm":
				case "avgEcpm":
					aVal = a.segmentMetrics?.avgCpm || 0;
					bVal = b.segmentMetrics?.avgCpm || 0;
					break;
				case "primaryPerson":
					aVal = a.segmentVector?.primaryPerson || a.primaryPerson || "";
					bVal = b.segmentVector?.primaryPerson || b.primaryPerson || "";
					break;
				case "strategy":
					aVal = a.segmentVector?.audienceStrategy || a.audienceStrategy || "";
					bVal = b.segmentVector?.audienceStrategy || b.audienceStrategy || "";
					break;
				case "activeUsers":
					aVal = a.segmentMetrics?.uniqueUsersWithRequests || 0;
					bVal = b.segmentMetrics?.uniqueUsersWithRequests || 0;
					break;
				case "activeIps":
					aVal = a.segmentMetrics?.uniqueIpsWithRequests || 0;
					bVal = b.segmentMetrics?.uniqueIpsWithRequests || 0;
					break;
				case "activeUsersWithImpressions":
					aVal = a.segmentMetrics?.uniqueUsersWithImpressions || 0;
					bVal = b.segmentMetrics?.uniqueUsersWithImpressions || 0;
					break;
				case "status":
					aVal = a.segmentMetrics?.latestStatus || "ready";
					bVal = b.segmentMetrics?.latestStatus || "ready";
					break;
				case "reportMonths":
					aVal = a.segmentMetrics?.reportMonths || a.reportMonths || "";
					bVal = b.segmentMetrics?.reportMonths || b.reportMonths || "";
					break;
				case "description":
					aVal = a.segmentVector?.description || a.description || "";
					bVal = b.segmentVector?.description || b.description || "";
					break;
				case "weightedClicks":
					aVal = a.segmentMetrics?.weightedClicks || 0;
					bVal = b.segmentMetrics?.weightedClicks || 0;
					break;
				case "weightedCprMarketImpressions":
					aVal = a.segmentMetrics?.weightedCprMarketImpressions || 0;
					bVal = b.segmentMetrics?.weightedCprMarketImpressions || 0;
					break;
				case "weightedFills":
					aVal = a.segmentMetrics?.weightedFills || 0;
					bVal = b.segmentMetrics?.weightedFills || 0;
					break;
				case "videoStarts":
					aVal = a.segmentMetrics?.weightedStartFirstOccurrence || 0;
					bVal = b.segmentMetrics?.weightedStartFirstOccurrence || 0;
					break;
				case "videoCompletions":
					aVal = a.segmentMetrics?.weightedCompleteFirstOccurrence || 0;
					bVal = b.segmentMetrics?.weightedCompleteFirstOccurrence || 0;
					break;
				case "weightedViewables":
					aVal = a.segmentMetrics?.weightedViewables || 0;
					bVal = b.segmentMetrics?.weightedViewables || 0;
					break;
				case "weightedSpendInUsd":
					aVal = a.segmentMetrics?.weightedSpendInUsd || 0;
					bVal = b.segmentMetrics?.weightedSpendInUsd || 0;
					break;
				case "avgSpendPerUniqueUser":
					aVal = a.segmentMetrics?.avgSpendPerUniqueUser || 0;
					bVal = b.segmentMetrics?.avgSpendPerUniqueUser || 0;
					break;
				default:
					aVal = showMultiScores ? (a.highestScaleScore || 0) : (a.compositeScore || 0);
					bVal = showMultiScores ? (b.highestScaleScore || 0) : (b.compositeScore || 0);
					break;
			}

			if (typeof aVal === "string") {
				return sortOrder === "asc"
					? aVal.localeCompare(bVal)
					: bVal.localeCompare(aVal);
			}

			return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
		});

		return filtered;
	}, [
		currentTabData,
		searchTerm,
		sortBy,
		sortOrder,
		categoryFilter,
		categoryDataMap,
	]);

	// Pagination
	const totalItems = filteredAndSortedData.length;
	const totalPages = Math.ceil(totalItems / pageSize);
	const startIndex = (currentPage - 1) * pageSize;
	const currentPageData = filteredAndSortedData.slice(
		startIndex,
		startIndex + pageSize,
	);

	// Event handlers
	const handleSort = (column) => {
		if (sortBy === column) {
			setSortOrder(sortOrder === "asc" ? "desc" : "asc");
		} else {
			setSortBy(column);
			setSortOrder("desc");
		}
	};

	const handleTabChange = (tabKey) => {
		setActiveTab(tabKey);
		setCurrentPage(1);
		setSearchTerm("");
		// Category filter persists across tabs - don't reset it
	};

	const handleSearchChange = (value) => {
		setSearchTerm(value);
		setCurrentPage(1);
	};

	const handleCategoryFilterChange = (newFilter) => {
		setCategoryFilter(newFilter);
		setCurrentPage(1);
	};

	const handlePageChange = (page) => {
		setCurrentPage(page);
	};

	const handlePageSizeChange = (newSize) => {
		setPageSize(newSize);
		setCurrentPage(1);
	};

	// Updated segment details handler to pass categoryDataMap
	const showSegmentDetails = (segment) => {
		console.log(
			"🎯 Opening segment details modal for:",
			segment.segmentVector?.segmentName,
		);
		setSelectedSegment(segment);
		setShowModal(true);
	};

	const showSetDetailsModal = (setKey, setData) => {
		console.log(
			"Opening set details modal for:",
			setKey,
			"from tab:",
			activeTab,
		);
		setSelectedSetForModal(setKey);
		setSelectedSetData(setData);
		setShowSetDetails(true);
	};

	const clearSearch = () => {
		setSearchTerm("");
		setCurrentPage(1);
		setIsSearchVisible(false);
		// Reset category filter when clearing search
		setCategoryFilter("ALL");
	};

	// Loading state
	if (loading) {
		return (
			<div className="results-container">
				<div className="results-section">
					<h2 className="section-title">Segment Results</h2>
					<div className="loading-state">
						<div className="loading-spinner"></div>
						<p>Loading segments...</p>
					</div>
				</div>
			</div>
		);
	}

	// Error state
	if (error) {
		return (
			<div className="results-container">
				<div className="results-section">
					<h2 className="section-title">Segment Results</h2>
					<div className="error-state">
						<div className="error-icon">⚠️</div>
						<h3>Error Loading Results</h3>
						<p>{error}</p>
						<button
							onClick={() => window.location.reload()}
							className="retry-button"
						>
							Retry
						</button>
					</div>
				</div>
			</div>
		);
	}

	// Calculate total count from all non-demographic results using actual deduplicated data.length
	const totalSearchTermResults = Object.entries(segmentsData)
		.filter(([key]) => !key.startsWith("Demographic_"))
		.reduce((total, [key, data]) => total + (data?.data?.length || 0), 0);

	// Calculate total selected items (segments + sets) for unified button
	const totalSelectedItems = selectedSegments.length + selectedSets.length;
	const hasSelectedItems = totalSelectedItems > 0;

	// Create unified selection message
	const getSelectionMessage = () => {
		const messages = [];
		if (selectedSegments.length > 0) {
			messages.push(
				`${selectedSegments.length} segment${selectedSegments.length > 1 ? "s" : ""}`,
			);
		}
		if (selectedSets.length > 0) {
			messages.push(
				`${selectedSets.length} set${selectedSets.length > 1 ? "s" : ""}`,
			);
		}
		return messages.join(" and ");
	};

	// Unified handler that adds both segments and sets
	const handleAddToCampaign = () => {
		// Instead of calling the APIs directly, open the panel
		setShowCampaignPanel(true);
	};

	return (
		<div className="results-wrapper">
			{/* Main Results Container */}
			<div className="results-container">
				<div className="results-section">
					<div
						style={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "flex-start",
							marginBottom: "20px",
							flexWrap: "wrap",
							gap: "16px",
						}}
					>
						<h2 className="section-title">
							Segment Results
							<span className="results-count">
								({totalSearchTermResults || totalItems || 0} results
								{sortBy && (
									<span className="sort-status">
										{" • "} Sorted by{" "}
										{SORT_DISPLAY_NAMES[sortBy] || sortBy}{" "}
										({STRING_SORT_KEYS.has(sortBy)
											? (sortOrder === "asc" ? "A-Z" : "Z-A")
											: (sortOrder === "asc" ? "Low to High" : "High to Low")})
									</span>
								)}
								)
							</span>
							{sortBy && (
								<button
									onClick={() => {
										setSortBy(showMultiScores ? "highestScaleScore" : "compositeScore");
										setSortOrder("desc");
									}}
									className="clear-sort-btn"
									title="Clear sorting"
								>
									Clear Sort
								</button>
							)}
						</h2>

						{/* Category Filter - Top Right */}
						<div className="top-category-filter-container">
							<span className="filter-label">Show:</span>
							<div className="category-toggle">
								<button
									className={`toggle-btn ${categoryFilter === "OA" ? "active" : ""}`}
									onClick={() => handleCategoryFilterChange("OA")}
								>
									OA Only
								</button>
								<button
									className={`toggle-btn ${categoryFilter === "OAA" ? "active" : ""}`}
									onClick={() => handleCategoryFilterChange("OAA")}
								>
									OAA Only
								</button>
								<button
									className={`toggle-btn ${categoryFilter === "ALL" ? "active" : ""}`}
									onClick={() => handleCategoryFilterChange("ALL")}
								>
									All
								</button>
							</div>
						</div>
					</div>

					{/* Tabs - Only show SearchTerm_ results with effective reach */}
					{availableTabs.length > 1 && (
						<div className="search-tabs-container">
							{availableTabs.map((tab) => (
								<button
									key={tab.key}
									onClick={() => handleTabChange(tab.key)}
									className={`tab ${activeTab === tab.key ? "active" : ""}`}
								>
									<div className="tab-content">
										<span className="tab-name">{tab.name}</span>
										<span className="tab-count">({tab.count})</span>
									</div>
								</button>
							))}
						</div>
					)}

					{/* Scoring sub-tabs — one per API call when multiScoredData is present */}
					{multiScoredData && (
						<div style={{ display: "flex", gap: "2px", padding: "6px 0 0", borderBottom: "1px solid #e8e8e8", marginBottom: "6px" }}>
							{SCORING_TABS.map(({ key, label, color }) => {
								const count = multiScoredData[key]?.data?.[activeTab]?.data?.length ?? 0;
								const isActive = activeScoringTab === key;
								return (
									<button
										key={key}
										onClick={() => { setActiveScoringTab(key); setCurrentPage(1); }}
										style={{
											padding: "5px 14px",
											border: "none",
											borderBottom: isActive ? `2px solid ${color}` : "2px solid transparent",
											background: isActive ? `${color}15` : "none",
											cursor: "pointer",
											fontSize: "0.8rem",
											fontWeight: isActive ? 700 : 500,
											color: isActive ? color : "#666",
											borderRadius: "4px 4px 0 0",
											transition: "all 0.15s",
										}}
									>
										{label}
										<span style={{ marginLeft: "5px", fontSize: "0.7rem", color: isActive ? color : "#aaa" }}>
											({count})
										</span>
									</button>
								);
							})}
						</div>
					)}

					{/* Set View - Shows cumulative data for performance sets for the active tab */}
					{activeTab && Object.keys(extractedCumulativeData).length > 0 && (
						<EnhancedSetView
							cumulativeData={extractedCumulativeData}
							selectedSets={selectedSets}
							onSetSelect={onSetSelect}
							onSetAdd={onSetAdd}
							showSetDetails={showSetDetailsModal}
							activeTab={activeTab}
							segmentsData={segmentsData}
							onExportSet={(setKey, setData) => {
								console.log("Exporting set:", setKey, setData);
								onExportSet
									? onExportSet(setKey, setData)
									: onExport && onExport("summarized", "set", setKey);
							}}
						/>
					)}

					{/* Search and Controls */}
					<div className="controls">
						<div className="controls-left">
							{searchTerm &&
								filteredAndSortedData.length !== currentTabData.length && (
									<div className="search-results-info">
										Showing {filteredAndSortedData.length} of{" "}
										{currentTabData.length} segments
										{categoryFilter === "OA" && " (OA only)"}
										{categoryFilter === "OAA" && " (OAA only)"}
									</div>
								)}
						</div>

						{(() => {
							const hiddenCount = getHiddenSelectionsCount();
							if (hiddenCount > 0) {
								return (
									<div className="hidden-selections-info">
										<span className="warning-icon">⚠️</span>
										{hiddenCount} selected segment{hiddenCount > 1 ? "s" : ""}{" "}
										hidden by current filters
										<button
											className="clear-hidden-btn"
											onClick={() => {
												// Clear only the hidden selections
												const visibleSegmentIds = filteredAndSortedData.map(
													(segment) => segment.id,
												);
												const hiddenSelections = selectedSegments.filter(
													(id) => !visibleSegmentIds.includes(id),
												);
												hiddenSelections.forEach((segmentId) => {
													onSegmentSelect && onSegmentSelect(segmentId);
												});
											}}
										>
											Clear Hidden
										</button>
										<button
											className="show-all-btn"
											onClick={() => {
												// Clear filters to show all segments
												setSearchTerm("");
												setCategoryFilter("ALL");
												setCurrentPage(1);
											}}
										>
											Show All
										</button>
									</div>
								);
							}
							return null;
						})()}

						<div className="controls-right">
							{/* Effective Reach Metrics - Small inline version */}
							{availableTabs.length > 0 &&
								activeTab &&
								(() => {
									const currentTab = availableTabs.find(
										(tab) => tab.key === activeTab,
									);
									if (currentTab && currentTab.effectiveReach > 0) {
										return (
											<div className="inline-reach-metrics">
												<div className="inline-metric">
													<span className="inline-label">Effective Reach:</span>
													<span className="inline-value">
														{formatNumber(currentTab.effectiveReach)}
													</span>
												</div>
												<div className="inline-metric">
													<span className="inline-label">With Filters:</span>
													<span className="inline-value">
														{formatNumber(
															Math.round(currentTab.demographicFilteredReach),
														)}
													</span>
												</div>
											</div>
										);
									}
									return null;
								})()}

							<div className="results-count">
								{filteredAndSortedData.length} segments found
								{categoryFilter === "OA" && " (OA only)"}
								{categoryFilter === "OAA" && " (OAA only)"}
							</div>
						</div>
					</div>

					{/* Table View - Always shown */}
					<div className="results-table-container">
						<table className="results-table">
							<thead>
								<tr>
									<th className="checkbox-column">
										<input
											type="checkbox"
											checked={isAllSelected}
											onChange={onSelectAll}
											title="Select All"
										/>
									</th>
									<th className="name-column">
										{isSearchVisible ? (
											<div className="search-header-container">
												<input
													type="text"
													placeholder="Search segments..."
													value={searchTerm || ""}
													onChange={(e) => handleSearchChange(e.target.value)}
													onFocus={(e) => e.target.select()}
													className="audience-search-input"
													autoFocus
												/>
												<button
													className="close-search-btn"
													onClick={() => {
														clearSearch();
														setIsSearchVisible(false);
													}}
													title="Close search"
												>
													✖
												</button>
											</div>
										) : (
											<div className="audience-header">
												<div
													className={`sortable-header ${sortBy === "segmentName" ? "active" : ""}`}
													onClick={() => handleSort("segmentName")}
												>
													<span className="header-text">
														Clean Segments Name
													</span>
													<SortIcon
														column="segmentName"
														currentSortColumn={sortBy}
														sortDirection={sortOrder}
													/>
												</div>
												<button
													className="search-toggle-btn"
													onClick={() => setIsSearchVisible(true)}
													title="Search segments"
												>
													<SearchIcon />
												</button>
											</div>
										)}
									</th>
									<th className="audienceType-column">
										<div
											className={`sortable-header ${sortBy === "audienceType" ? "active" : ""}`}
											onClick={() => handleSort("audienceType")}
										>
											<span className="header-text">Audience Type</span>
											<SortIcon
												column="audienceType"
												currentSortColumn={sortBy}
												sortDirection={sortOrder}
											/>
										</div>
									</th>
									<th className="provider-column">
										<div
											className={`sortable-header ${sortBy === "provider" ? "active" : ""}`}
											onClick={() => handleSort("provider")}
										>
											<span className="header-text">Provider</span>
											<SortIcon
												column="provider"
												currentSortColumn={sortBy}
												sortDirection={sortOrder}
											/>
										</div>
									</th>
									<th className="category-column">
										<div
											className={`sortable-header ${sortBy === "category" ? "active" : ""}`}
											onClick={() => handleSort("category")}
										>
											<span className="header-text">Category</span>
											<SortIcon
												column="category"
												currentSortColumn={sortBy}
												sortDirection={sortOrder}
											/>
										</div>
									</th>
									{showMultiScores ? (
										<>
											<th className="multi-score-column">
												<div className={`sortable-header ${sortBy === "highestScaleScore" ? "active" : ""}`} onClick={() => handleSort("highestScaleScore")}>
													<span className="header-text">Highest Scale</span>
													<SortIcon column="highestScaleScore" currentSortColumn={sortBy} sortDirection={sortOrder} />
												</div>
											</th>
											<th className="multi-score-column">
												<div className={`sortable-header ${sortBy === "bestPerformanceScore" ? "active" : ""}`} onClick={() => handleSort("bestPerformanceScore")}>
													<span className="header-text">Best Perf.</span>
													<SortIcon column="bestPerformanceScore" currentSortColumn={sortBy} sortDirection={sortOrder} />
												</div>
											</th>
											<th className="multi-score-column">
												<div className={`sortable-header ${sortBy === "mostRelevantScore" ? "active" : ""}`} onClick={() => handleSort("mostRelevantScore")}>
													<span className="header-text">Most Relevant</span>
													<SortIcon column="mostRelevantScore" currentSortColumn={sortBy} sortDirection={sortOrder} />
												</div>
											</th>
										</>
									) : (
										<th className="composite-score-column">
											<div
												className={`sortable-header ${sortBy === "compositeScore" ? "active" : ""}`}
												onClick={() => handleSort("compositeScore")}
											>
												<span className="header-text">Composite Score</span>
												<SortIcon
													column="compositeScore"
													currentSortColumn={sortBy}
													sortDirection={sortOrder}
												/>
											</div>
										</th>
									)}
									<th className="search-score-column">
										<div
											className={`sortable-header ${sortBy === "searchScore" ? "active" : ""}`}
											onClick={() => handleSort("searchScore")}
										>
											<span className="header-text">Relevance</span>
											<SortIcon
												column="searchScore"
												currentSortColumn={sortBy}
												sortDirection={sortOrder}
											/>
										</div>
									</th>
									<th className="reach-column">
										<div
											className={`sortable-header ${sortBy === "calculatedReach" ? "active" : ""}`}
											onClick={() => handleSort("calculatedReach")}
										>
											<span className="header-text">Reach</span>
											<SortIcon
												column="calculatedReach"
												currentSortColumn={sortBy}
												sortDirection={sortOrder}
											/>
										</div>
									</th>
									<th className="reach-column">
										<div
											className={`sortable-header ${sortBy === "uniqueUsers" ? "active" : ""}`}
											onClick={() => handleSort("uniqueUsers")}
										>
											<span className="header-text">Unique Users</span>
											<SortIcon
												column="uniqueUsers"
												currentSortColumn={sortBy}
												sortDirection={sortOrder}
											/>
										</div>
									</th>
									<th className="ecpm-column">
										<div
											className={`sortable-header ${sortBy === "avgEcpm" ? "active" : ""}`}
											onClick={() => handleSort("avgEcpm")}
										>
											<span className="header-text">Avg eCPM</span>
											<SortIcon
												column="avgEcpm"
												currentSortColumn={sortBy}
												sortDirection={sortOrder}
											/>
										</div>
									</th>
									<th className="status-column">
										<div
											className={`sortable-header ${sortBy === "status" ? "active" : ""}`}
											onClick={() => handleSort("status")}
										>
											<span className="header-text">Status</span>
											<SortIcon
												column="status"
												currentSortColumn={sortBy}
												sortDirection={sortOrder}
											/>
										</div>
									</th>
									<th className="report-months-column">
										<div
											className={`sortable-header ${sortBy === "reportMonths" ? "active" : ""}`}
											onClick={() => handleSort("reportMonths")}
										>
											<span className="header-text">Report Months</span>
											<SortIcon
												column="reportMonths"
												currentSortColumn={sortBy}
												sortDirection={sortOrder}
											/>
										</div>
									</th>
									<th className="description-column">
										<div
											className={`sortable-header ${sortBy === "description" ? "active" : ""}`}
											onClick={() => handleSort("description")}
										>
											<span className="header-text">Notes/Description</span>
											<SortIcon
												column="description"
												currentSortColumn={sortBy}
												sortDirection={sortOrder}
											/>
										</div>
									</th>
									<th className="clicks-column">
										<div
											className={`sortable-header ${sortBy === "weightedClicks" ? "active" : ""}`}
											onClick={() => handleSort("weightedClicks")}
										>
											<span className="header-text">Clicks</span>
											<SortIcon
												column="clicks"
												currentSortColumn={sortBy}
												sortDirection={sortOrder}
											/>
										</div>
									</th>
									<th className="impressions-column">
										<div
											className={`sortable-header ${sortBy === "weightedImpressions" ? "active" : ""}`}
											onClick={() => handleSort("weightedImpressions")}
										>
											<span className="header-text">Impressions</span>
											<SortIcon
												column="impressions"
												currentSortColumn={sortBy}
												sortDirection={sortOrder}
											/>
										</div>
									</th>
									<th className="activeUsers-column">
										<div
											className={`sortable-header ${sortBy === "activeUsers" ? "active" : ""}`}
											onClick={() => handleSort("activeUsers")}
										>
											<span className="header-text">Active Users</span>
											<SortIcon
												column="activeUsers"
												currentSortColumn={sortBy}
												sortDirection={sortOrder}
											/>
										</div>
									</th>
									<th className="activeIps-column">
										<div
											className={`sortable-header ${sortBy === "activeIps" ? "active" : ""}`}
											onClick={() => handleSort("activeIps")}
										>
											<span className="header-text">Active IPs</span>
											<SortIcon
												column="activeIps"
												currentSortColumn={sortBy}
												sortDirection={sortOrder}
											/>
										</div>
									</th>
									<th className="activeUsersWithImpressions-column">
										<div
											className={`sortable-header ${sortBy === "activeUsersWithImpressions" ? "active" : ""}`}
											onClick={() => handleSort("activeUsersWithImpressions")}
										>
											<span className="header-text">
												Active Users with Impressions
											</span>
											<SortIcon
												column="activeUsersWithImpressions"
												currentSortColumn={sortBy}
												sortDirection={sortOrder}
											/>
										</div>
									</th>
									<th className="fills-column">
										<div
											className={`sortable-header ${sortBy === "weightedFills" ? "active" : ""}`}
											onClick={() => handleSort("weightedFills")}
										>
											<span className="header-text">Fills</span>
											<SortIcon
												column="fills"
												currentSortColumn={sortBy}
												sortDirection={sortOrder}
											/>
										</div>
									</th>
									<th className="video-starts-column">
										<div
											className={`sortable-header ${sortBy === "videoStarts" ? "active" : ""}`}
											onClick={() => handleSort("videoStarts")}
										>
											<span className="header-text">Video Starts</span>
											<SortIcon
												column="videoStarts"
												currentSortColumn={sortBy}
												sortDirection={sortOrder}
											/>
										</div>
									</th>
									<th className="video-completions-column">
										<div
											className={`sortable-header ${sortBy === "videoCompletions" ? "active" : ""}`}
											onClick={() => handleSort("videoCompletions")}
										>
											<span className="header-text">Video Completions</span>
											<SortIcon
												column="videoCompletions"
												currentSortColumn={sortBy}
												sortDirection={sortOrder}
											/>
										</div>
									</th>
									<th className="viewable-impressions-column">
										<div
											className={`sortable-header ${sortBy === "weightedViewables" ? "active" : ""}`}
											onClick={() => handleSort("weightedViewables")}
										>
											<span className="header-text">Viewable Impressions</span>
											<SortIcon
												column="viewableImpressions"
												currentSortColumn={sortBy}
												sortDirection={sortOrder}
											/>
										</div>
									</th>
									<th className="spend-column">
										<div
											className={`sortable-header ${sortBy === "weightedSpendInUsd" ? "active" : ""}`}
											onClick={() => handleSort("weightedSpendInUsd")}
										>
											<span className="header-text">Spend ($)</span>
											<SortIcon
												column="spend"
												currentSortColumn={sortBy}
												sortDirection={sortOrder}
											/>
										</div>
									</th>
									<th className="avg-spend-per-user-column">
										<div
											className={`sortable-header ${sortBy === "avgSpendPerUniqueUser" ? "active" : ""}`}
											onClick={() => handleSort("avgSpendPerUniqueUser")}
										>
											<span className="header-text">Avg Spend Per User</span>
											<SortIcon
												column="avgSpendPerUser"
												currentSortColumn={sortBy}
												sortDirection={sortOrder}
											/>
										</div>
									</th>
								</tr>
							</thead>
							<tbody>
								{currentPageData && currentPageData.length > 0 ? (
									currentPageData.map((segment) => {
										const segmentType = getSegmentTypeHelper(
											segment,
											categoryDataMap,
										);
										return (
											<tr key={segment.id} className="result-row">
												<td className="checkbox-cell">
													<input
														type="checkbox"
														checked={selectedSegments.includes(segment.id)}
														onChange={() =>
															onSegmentSelect && onSegmentSelect(segment.id)
														}
													/>
												</td>
												<td className="name-cell">
													<div className="segment-name-container">
														<span
															className="segment-name"
															onMouseEnter={(e) =>
																handleSegmentNameHover(segment, e)
															}
															onMouseLeave={handleSegmentNameLeave}
															title="" // Remove default browser tooltip since we're using custom
														>
															{segment.segmentVector?.segmentName ||
																"Unknown Segment"}
														</span>
														<div
															className="info-icon-wrapper"
															onClick={() => showSegmentDetails(segment)}
														>
															<button
																className="info-icon"
																title="View detailed metrics and audience data"
															>
																i
															</button>
														</div>
													</div>
												</td>
												<td className="audienceType-cell">
													{segment.segmentVector?.audienceType ||
														segment.audienceType ||
														"-"}
												</td>
												<td className="provider-cell">
													{segment.segmentVector?.providerId ||
														segment.provider ||
														"-"}
												</td>
												<td className="category-cell">
													<span
														className={`category-badge ${segmentType.toLowerCase()}`}
													>
														{segmentType}
													</span>
												</td>
												{showMultiScores ? (
													<>
														<td className="multi-score-cell">{segment.highestScaleScore != null ? segment.highestScaleScore.toFixed(3) : "—"}</td>
														<td className="multi-score-cell">{segment.bestPerformanceScore != null ? segment.bestPerformanceScore.toFixed(3) : "—"}</td>
														<td className="multi-score-cell">{segment.mostRelevantScore != null ? segment.mostRelevantScore.toFixed(3) : "—"}</td>
													</>
												) : (
													<td className="composite-score-cell">
														<div className="score-bar-container">
															<div
																className={`score-bar ${getCompositeScoreClass(segment.compositeScore)}`}
																style={{
																	width: `${Math.min(100, segment.compositeScore || 0)}%`,
																}}
															></div>
															<span className="score-text">
																{segment.compositeScore
																	? segment.compositeScore.toFixed(1)
																	: "N/A"}
															</span>
														</div>
													</td>
												)}
												<td className="search-score-cell">
													<div className="score-bar-container">
														<span className="score-text">
															{(() => {
																const rawScore = segment.score || 0;
																return relevanceFromDistance(
																	rawScore,
																	"SEGMENTS",
																).toFixed(1);
															})()}
														</span>
													</div>
												</td>
												<td className="reach-cell">
													{segment.segmentMetrics?.calculatedReach}
												</td>
												<td className="reach-cell">
													{segment.segmentMetrics?.uniqueUsers}
												</td>
												<td className="ecpm-cell">
													${(segment.segmentMetrics?.avgCpm || 0).toFixed(2)}
												</td>
												<td className="status-cell">
													<span
														className={`status-badge ${getStatusClass(segment.segmentMetrics?.latestStatus || "ready")}`}
													>
														{segment.segmentMetrics?.latestStatus || "ready"}
													</span>
												</td>
												<td className="report-months-cell">
													{segment.segmentMetrics?.reportMonths ||
														segment.reportMonths ||
														"-"}{" "}
													Months
												</td>
												<td className="description-cell">
													<span
														title={
															segment.segmentVector?.description ||
															segment.description ||
															""
														}
													>
														{(
															segment.segmentVector?.description ||
															segment.description ||
															""
														).substring(0, 50)}
														{(
															segment.segmentVector?.description ||
															segment.description ||
															""
														).length > 50
															? "..."
															: ""}
													</span>
												</td>
												<td className="clicks-cell">
													{formatNumber(
														segment.segmentMetrics?.weightedClicks || 0,
													)}
												</td>
												<td className="impressions-cell">
													{formatNumber(
														segment.segmentMetrics?.weightedImpressions || 0,
													)}
												</td>
												<td className="active-users-cell">
													{formatNumber(
														segment.segmentMetrics?.uniqueUsersWithRequests ||
															0,
													)}
												</td>
												<td className="active-ip-cell">
													{formatNumber(
														segment.segmentMetrics?.uniqueIpsWithRequests || 0,
													)}
												</td>
												<td className="active-users-with-impressions-cell">
													{formatNumber(
														segment.segmentMetrics
															?.uniqueUsersWithImpressions || 0,
													)}
												</td>
												<td className="fills-cell">
													{formatNumber(
														segment.segmentMetrics?.weightedFills || 0,
													)}
												</td>
												<td className="video-starts-cell">
													{formatNumber(
														segment.segmentMetrics
															?.weightedStartFirstOccurrence || 0,
													)}
												</td>
												<td className="video-completions-cell">
													{formatNumber(
														segment.segmentMetrics
															?.weightedCompleteFirstOccurrence || 0,
													)}
												</td>
												<td className="viewable-impressions-cell">
													{formatNumber(
														segment.segmentMetrics?.weightedViewables || 0,
													)}
												</td>
												<td className="spend-cell">
													$
													{(
														segment.segmentMetrics?.weightedSpendInUsd /
															1000000 || 0
													).toFixed(2)}
												</td>
												<td className="avg-spend-per-user-cell">
													$
													{Number(
														segment.segmentMetrics?.avgSpendPerUniqueUser || 0,
													).toFixed(2)}
												</td>
											</tr>
										);
									})
								) : (
									<tr className="empty-results-row">
										<td colSpan={20} className="empty-results-cell">
											<div className="empty-results-content">
												<h3>No Results Found</h3>
												<p>
													{searchTerm
														? `No results match "${searchTerm}". Try adjusting your search.`
														: categoryFilter === "OA"
															? 'No OA segments found. Try switching to "All" or "OAA Only" or adjusting your filters.'
															: categoryFilter === "OAA"
																? 'No OAA segments found. Try switching to "All" or "OA Only" or adjusting your filters.'
																: "No segment data available. Try running a search or adjusting your filters."}
												</p>
												{(searchTerm || categoryFilter !== "ALL") && (
													<button
														onClick={clearSearch}
														className="clear-search-button"
													>
														Clear Filters
													</button>
												)}
											</div>
										</td>
									</tr>
								)}
							</tbody>
						</table>
						{hoveredSegment && (
							<div
								className="segment-name-tooltip"
								style={{
									left: `${tooltipPosition.x}px`,
									top: `${tooltipPosition.y}px`,
								}}
							>
								{hoveredSegment.segmentVector?.fullName || "Unknown Segment"}
							</div>
						)}
					</div>

					{/* Pagination and Export Row */}
					<div className="pagination-export-row">
						<div className="export-section">
							<EnhancedSegmentsExportDropdown
								isExporting={isExporting}
								onExport={onExport}
								currentTabData={currentTabData}
								totalTabsData={segmentsData}
								selectedCount={selectedSegments.length}
								activeTab={activeTab}
							/>
						</div>

						<div className="pagination-section">
							<Pagination
								currentPage={currentPage}
								totalPages={totalPages}
								totalItems={totalItems}
								itemsPerPage={pageSize}
								onPageChange={handlePageChange}
								onPageSizeChange={handlePageSizeChange}
								loading={loading}
								showJumpToPage={true}
								showPageSizeSelector={true}
								showResultsInfo={true}
								forceShow={totalItems > 0}
							/>
						</div>
					</div>

					{/* Action Buttons - Unified for both segments and sets */}
					{hasSelectedItems && (
						<div className="action-buttons">
							<button
								onClick={handleAddToCampaign}
								className="bulk-action-btn unified-action-btn"
							>
								Add {getSelectionMessage()} to Campaign
							</button>
						</div>
					)}
				</div>

				{/* Enhanced Segment Detail Modal */}
				<EnhancedSegmentDetailModal
					segment={selectedSegment}
					isOpen={showModal}
					onClose={() => setShowModal(false)}
					categoryDataMap={categoryDataMap}
				/>

				{/* Separated Set Details Modal */}
				<SetDetailsModal
					setKey={selectedSetForModal}
					setData={selectedSetData}
					segmentsData={segmentsData}
					activeTab={activeTab}
					demographicProbabilities={demographicProbabilities}
					isOpen={showSetDetails}
					onClose={() => {
						setShowSetDetails(false);
						setSelectedSetForModal(null);
						setSelectedSetData(null);
					}}
					onSegmentSelect={onSegmentSelect}
					selectedSegments={selectedSegments}
					categoryDataMap={categoryDataMap}
				/>
			</div>

			<CampaignAnalysisPanel
				isOpen={showCampaignPanel}
				onClose={() => setShowCampaignPanel(false)}
				selectedSegments={selectedSegments}
				selectedSets={selectedSets}
				segmentsData={segmentsData}
				cumulativeData={extractedCumulativeData}
				demographicData={demographicData}
				logicChain={logicChain}
				searchTerms={searchTerms}
				filters={currentFilters}
			/>

			{/* Updated Styles to match Audience Results List */}
			<style jsx>{`
            /* Hidden selections indicator styles */
.hidden-selections-info {
    font-size: 13px;
    color: #dc3545;
    background: #fff5f5;
    padding: 8px 12px;
    border-radius: 6px;
    border: 1px solid #fecaca;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-right: 12px;
}

.warning-icon {
    font-size: 14px;
}

.clear-hidden-btn, .show-all-btn {
    padding: 4px 8px;
    font-size: 11px;
    border: 1px solid #dc3545;
    background: white;
    color: #dc3545;
    border-radius: 4px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s;
}

.clear-hidden-btn:hover, .show-all-btn:hover {
    background: #dc3545;
    color: white;
}

.show-all-btn {
    border-color: #0076BD;
    color: #0076BD;
}

.show-all-btn:hover {
    background: #0076BD;
    color: white;
}
    .segment-name {
        font-weight: 500;
        color: #212529;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        flex: 1;
        cursor: default; /* Add cursor indication */
        position: relative;
}

.segment-name:hover {
    color: #0076BD; /* Add hover color change for better UX */
}

.segment-name-tooltip {
    position: fixed;
    background: #1a1a1a;
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    max-width: 400px;
    word-wrap: break-word;
    white-space: normal;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 9999;
    pointer-events: none;
    transform: translateX(-50%);
    line-height: 1.3;
    
    /* Add arrow pointing down to the segment name */
    &::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border-left: 8px solid transparent;
        border-right: 8px solid transparent;
        border-top: 8px solid #1a1a1a;
    }
    
    /* Animation */
    animation: tooltipFadeIn 0.2s ease-out;
}

@keyframes tooltipFadeIn {
    from {
        opacity: 0;
        transform: translateX(-50%) translateY(5px);
    }
    to {
        opacity: 1;
        transform: translateX(-50%) translateY(0);
    }
}

/* Enhanced Set View Styles */
.enhanced-set-view-container {
    margin: 0 0 24px 0;
    padding: 20px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.enhanced-set-view-container .set-view-header {
    margin-bottom: 20px;
    text-align: center;
}

.enhanced-set-view-container .set-view-header h3 {
    margin: 0 0 8px 0;
    font-size: 18px;
    font-weight: 600;
    color: #1e293b;
}

.enhanced-set-view-container .set-view-description {
    margin: 0;
    font-size: 14px;
    color: #64748b;
    line-height: 1.5;
}

.enhanced-sets-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    align-items: stretch;
}

.enhanced-set-card {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 0;
    transition: all 0.3s ease;
    position: relative;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.enhanced-set-card:hover {
    border-color: #3b82f6;
    box-shadow: 0 8px 25px rgba(59, 130, 246, 0.15);
    transform: translateY(-2px);
}

.enhanced-set-card .set-card-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 16px 16px 0 16px;
}

.enhanced-set-card .set-checkbox {
    display: flex;
    align-items: center;
}

.enhanced-set-card .set-checkbox input[type="checkbox"] {
    width: 18px;
    height: 18px;
    cursor: pointer;
    accent-color: #3b82f6;
}

.relevance-circle {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 8px rgba(34, 197, 94, 0.3);
}

.relevance-circle[data-score="85"],
.relevance-circle[data-score="86"] {
    background: linear-gradient(135deg, #059669 0%, #047857 100%);
    box-shadow: 0 2px 8px rgba(5, 150, 105, 0.3);
}

.relevance-circle[data-score="80"] {
    background: linear-gradient(135deg, #0891b2 0%, #0e7490 100%);
    box-shadow: 0 2px 8px rgba(8, 145, 178, 0.3);
}

.circle-content {
    display: flex;
    align-items: center;
    justify-content: center;
}

.circle-content .score {
    font-size: 16px;
    font-weight: 700;
    color: white;
}

.enhanced-set-card .set-card-content {
    flex: 1;
    padding: 16px;
    cursor: pointer;
}

.enhanced-set-card .set-name {
    margin: 0 0 8px 0;
    font-size: 16px;
    font-weight: 600;
    color: #1e293b;
    line-height: 1.3;
}

.enhanced-set-card .set-description {
    margin: 0 0 16px 0;
    font-size: 12px;
    color: #64748b;
    line-height: 1.4;
}

.primary-metrics-row,
.secondary-metrics-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 12px;
    gap: 8px;
}

.primary-metrics-row .metric-group,
.secondary-metrics-row .metric-group {
    flex: 1;
    text-align: center;
}

.primary-metrics-row .metric-label,
.secondary-metrics-row .metric-label {
    font-size: 9px;
    color: #64748b;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
}

.primary-metrics-row .metric-value,
.secondary-metrics-row .metric-value {
    font-size: 12px;
    color: #1e293b;
    font-weight: 700;
    line-height: 1.2;
}

.set-card-actions {
    display: flex;
    padding: 12px 16px;
    gap: 8px;
    border-top: 1px solid #f1f5f9;
    background: #fafbfc;
}

.action-btn {
    flex: 1;
    padding: 8px 12px;
    border: 1px solid #e2e8f0;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    text-align: center;
}

.view-details {
    background: white;
    color: #475569;
}

.view-details:hover {
    background: #f8fafc;
    border-color: #3b82f6;
    color: #3b82f6;
}

.export-btn {
    background: #22d3ee;
    color: white;
    border-color: #22d3ee;
}

.export-btn:hover {
    background: #06b6d4;
    border-color: #06b6d4;
}

.placeholder-card {
    background: #f8fafc;
    border: 2px dashed #cbd5e1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    opacity: 0.6;
}

.placeholder-content {
    text-align: center;
    color: #94a3b8;
}

.placeholder-text {
    font-size: 14px;
    font-weight: 500;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
    .enhanced-sets-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 14px;
    }
}

@media (max-width: 768px) {
    .enhanced-sets-grid {
        grid-template-columns: 1fr;
        gap: 12px;
    }

    .enhanced-set-view-container {
        padding: 16px;
        margin-bottom: 20px;
    }

    .enhanced-set-view-container .set-view-header h3 {
        font-size: 16px;
    }

    .enhanced-set-view-container .set-view-description {
        font-size: 13px;
    }

    .primary-metrics-row,
    .secondary-metrics-row {
        gap: 4px;
    }

    .primary-metrics-row .metric-value,
    .secondary-metrics-row .metric-value {
        font-size: 11px;
    }

    .relevance-circle {
        width: 42px;
        height: 42px;
    }

    .circle-content .score {
        font-size: 14px;
    }

    .enhanced-set-card .set-name {
        font-size: 15px;
    }

    .enhanced-set-card .set-description {
        font-size: 11px;
    }
}

.placeholder-card {
    background: #f8fafc;
    border: 2px dashed #cbd5e1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    opacity: 0.6;
}

.placeholder-content {
    text-align: center;
    color: #94a3b8;
}

.placeholder-text {
    font-size: 14px;
    font-weight: 500;
}


.metric-info {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 0;
}

.metric-label {
    font-size: 10px;
    color: #64748b;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 2px;
}

.metric-value {
    font-size: 14px;
    color: #1e293b;
    font-weight: 700;
    line-height: 1;
}

/* Main Results Container */
.results-wrapper {
    width: 100%;
}

.results-container {
    background: white;
    border-radius: 8px;
    border: 1px solid #e5e7eb;
    overflow: visible;
}

.results-section {
    overflow: visible;
    position: relative;
    z-index: 1;
}

.section-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 18px;
    font-weight: 600;
    color: #212529;
    margin: 0;
}

.results-count {
    font-size: 14px;
    color: #666;
    font-weight: normal;
}

.sort-status {
    color: #0076BD;
    font-weight: 500;
}

.clear-sort-btn {
    font-size: 12px;
    color: #6c757d;
    background: none;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 4px 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-left: 8px;
    font-weight: normal;
}

/* Top Category Filter */
.top-category-filter-container {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
}

.filter-label {
    font-size: 14px;
    color: #374151;
    font-weight: 500;
}

.category-toggle {
    display: flex;
    background: #f3f4f6;
    border-radius: 6px;
    padding: 2px;
    border: 1px solid #e5e7eb;
}

.toggle-btn {
    padding: 6px 12px;
    border: none;
    background: none;
    color: #6b7280;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.2s;
    white-space: nowrap;
}

.toggle-btn:hover {
    color: #374151;
    background: #e5e7eb;
}

.toggle-btn.active {
    background: #3b82f6;
    color: white;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

/* Search Tabs */
.search-tabs-container {
    display: flex;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    overflow-x: auto;
    margin-bottom: 20px;
}

.search-tabs-container .tab {
    padding: 12px 20px;
    border: none;
    background: none;
    color: #6b7280;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    border-bottom: 2px solid transparent;
    transition: all 0.2s;
}

.search-tabs-container .tab:hover {
    color: #374151;
    background: #f3f4f6;
}

.search-tabs-container .tab.active {
    color: #3b82f6;
    border-bottom-color: #3b82f6;
    background: white;
}

.search-tabs-container .tab-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
}

.search-tabs-container .tab-name {
    font-size: 14px;
}

.search-tabs-container .tab-count {
    font-size: 12px;
    opacity: 0.8;
}

/* Controls */
.controls {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px;
    background: #f9fafb;
    border-bottom: 1px solid #e5e7eb;
    margin-bottom: 20px;
}

.controls-left {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 16px;
    flex-wrap: wrap;
}

.search-results-info {
    font-size: 14px;
    color: #3b82f6;
    font-weight: 500;
    background: #eff6ff;
    padding: 6px 12px;
    border-radius: 6px;
}

.controls-right {
    display: flex;
    align-items: center;
    gap: 16px;
}

.inline-reach-metrics {
    display: flex;
    gap: 12px;
    align-items: center;
}

.inline-metric {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 4px;
}

.inline-label {
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
}

.inline-value {
    font-size: 12px;
    color: #1e293b;
    font-weight: 600;
}

/* Table */
.results-table-container {
    overflow-x: auto;
    overflow-y: visible;
    position: static;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    background-color: white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    margin-bottom: 0;
    max-width: 100%;
}

.results-table {
    position: static;
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    font-size: 14px;
    min-width: 2800px;
}

/* Column Widths */
.results-table .checkbox-column { width: 40px; }
.results-table .name-column { width: 250px; }
.results-table .audienceType-column { width: 100px; }
.results-table .provider-column { width: 120px; }
.results-table .category-column { width: 80px; }
.results-table .composite-score-column { width: 120px; }
.results-table .multi-score-column { width: 80px; }
.multi-score-cell { text-align: right; font-size: 0.78rem; font-weight: 600; color: #444; padding: 0 8px; }
.results-table .search-score-column { width: 120px; }
.results-table .impressions-column { width: 120px; }
.results-table .reach-column { width: 110px; }
.results-table .ecpm-column { width: 90px; }
.results-table .status-column { width: 80px; }
.results-table .report-months-column { width: 80px; }
.results-table .description-column { width: 200px; }
.results-table .spend-column { width: 100px; }
.results-table .video-starts-column { width: 100px; }
.results-table .activeIps-column { width: 140px; }

.results-table th {
    background-color: #f8f9fa;
    border-bottom: 2px solid #dee2e6;
    padding: 12px 8px;
    text-align: left;
    font-weight: 600;
    font-size: 12px;
    color: #495057;
    white-space: nowrap;
    position: static;
    overflow: hidden;
}

.results-table td {
    padding: 12px 8px;
    border-bottom: 1px solid #e9ecef;
    vertical-align: middle;
    text-overflow: ellipsis;
    white-space: nowrap;
    position: static;
    overflow: hidden;
}

.result-row {
    transition: background-color 0.2s ease;
}

.result-row:hover {
    background-color: #f8f9fa;
}

.result-row.selected {
    background-color: #e3f2fd;
}

/* Name Cell - Original Design */
.name-cell {
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 250px;
}

.segment-name-container {
    display: flex;
    align-items: center;
    gap: 8px;
    position: static;
    min-width: 0;
}

.segment-name {
    font-weight: 500;
    color: #212529;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
}

.info-icon-wrapper {
    position: static;
    display: inline-flex;
    flex-shrink: 0;
}

.info-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background-color: #6c757d;
    color: white;
    border: none;
    font-size: 11px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.2s;
}

.info-icon:hover {
    background-color: #495057;
    color: white;
}

/* Header Styling */
.audience-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    min-width: 0;
}

.sortable-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    user-select: none;
    transition: all 0.2s;
    padding: 2px 4px;
    border-radius: 3px;
    flex: 1;
    min-width: 0;
}

.sortable-header:hover {
    background-color: rgba(0, 118, 189, 0.1);
}

.sortable-header.active {
    background-color: rgba(0, 118, 189, 0.15);
    color: #0076BD;
    font-weight: 600;
}

.sortable-header .header-text {
    flex: 1;
    min-width: 0;
}

.sort-icon {
    display: inline-flex;
    align-items: center;
    margin-left: 4px;
    opacity: 0.6;
    transition: opacity 0.2s;
    flex-shrink: 0;
}

.sortable-header:hover .sort-icon,
.sortable-header.active .sort-icon {
    opacity: 1;
}

.sort-icon svg {
    width: 12px;
    height: 12px;
}

.search-toggle-btn {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 12px;
    padding: 4px;
    border-radius: 3px;
    transition: background-color 0.2s;
    flex-shrink: 0;
    margin-left: 4px;
}

.search-toggle-btn:hover {
    background-color: rgba(0,0,0,0.1);
}

.search-header-container {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
}

.audience-search-input {
    width: 100%;
    padding: 3px 20px 3px 6px;
    border: 1px solid #d1d5db;
    border-radius: 3px;
    font-size: 11px;
    background-color: white;
    transition: border-color 0.2s;
    height: 22px;
    line-height: 1;
}

.audience-search-input:focus {
    outline: none;
    border-color: #0076BD;
    box-shadow: 0 0 0 2px rgba(0, 118, 189, 0.1);
}

.close-search-btn {
    position: absolute;
    right: 3px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: #6b7280;
    cursor: pointer;
    font-size: 14px;
    line-height: 1;
    padding: 1px 2px;
    border-radius: 2px;
    transition: color 0.2s;
    height: 16px;
    width: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.close-search-btn:hover {
    color: #374151;
    background-color: #f3f4f6;
}

/* Score Bar Styling */
.score-bar-container {
    position: relative;
    width: 100%;
    height: 20px;
    background-color: #f8f9fa;
    border-radius: 10px;
    overflow: hidden;
    display: flex;
    align-items: center;
}

.score-bar {
    height: 100%;
    transition: width 0.3s ease;
    border-radius: 10px;
}

.score-bar.excellent { 
    background-color: #28a745;
}
.score-bar.great { 
    background-color: #17a2b8;
}
.score-bar.acceptable { 
    background-color: #ffc107;
}
.score-bar.poor { 
    background-color: #fd7e14;
}
.score-bar.irrelevant { 
    background-color: #6c757d;
}

.score-text {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    font-size: 11px;
    font-weight: 600;
    color: #212529;
    text-shadow: 0 1px 2px rgba(255,255,255,0.8);
    z-index: 1;
}

/* Other Cell Styles */
.checkbox-cell, .checkbox-column {
    text-align: center;
    padding: 8px;
}

.composite-score-cell, .composite-score-column,
.search-score-cell, .search-score-column,
.reach-cell, .reach-column,
.ecpm-cell, .ecpm-column,
.status-cell, .status-column {
    text-align: center;
}

.audienceType-cell, .provider-cell,
.report-months-cell {
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.description-cell {
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 200px;
}

.category-badge {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.category-badge.oa {
    background-color: #dcfce7;
    color: #166534;
}

.category-badge.oaa {
    background-color: #fef3c7;
    color: #92400e;
}

.status-badge {
    display: inline-block;
    padding: 3px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.status-success {
    background-color: #d4edda;
    color: #155724;
}

.status-error {
    background-color: #f8d7da;
    color: #721c24;
}

.status-unknown {
    background-color: #fff3cd;
    color: #856404;
}

/* Pagination Export Row */
.pagination-export-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 12px 20px;
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;
    gap: 16px;
    flex-wrap: wrap;
    margin-top: 0;
}

.export-section {
    display: flex;
    align-items: center;
    gap: 12px;
    position: relative;
}

.pagination-section {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-left: auto;
}

.export-dropdown {
    position: relative;
    z-index: 1001;
}

.dropdown-item {
    width: 100%;
    padding: 8px 12px;
    border: none;
    background: none;
    text-align: left;
    cursor: pointer;
    font-size: 14px;
    color: #212529;
    transition: background-color 0.2s;
}

/* Pagination */
.pagination-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
    border-top: 1px solid #e5e7eb;
    background: #f9fafb;
}

.pagination-controls {
    display: flex;
    align-items: center;
    gap: 12px;
}

.pagination-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
}

.pagination-btn:hover:not(:disabled) {
    background: #f3f4f6;
    border-color: #9ca3af;
}

.pagination-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.pagination-current {
    font-size: 14px;
    color: #374151;
    font-weight: 500;
}

.pagination-info {
    font-size: 14px;
    color: #6b7280;
}

.page-size-select {
    padding: 6px 8px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 14px;
}

/* Action Buttons */
.action-buttons {
    display: flex;
    gap: 12px;
    align-items: center;
    margin-top: 20px;
    padding: 16px;
    background-color: #f8f9fa;
    border-radius: 8px;
    border: 1px solid #dee2e6;
}

.bulk-action-btn {
    padding: 8px 16px;
    background: #3b82f6;
    color: white;
    border: none;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
}

.bulk-action-btn:hover {
    background: #2563eb;
}

.unified-action-btn {
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
    box-shadow: 0 1px 3px rgba(59, 130, 246, 0.2);
    font-weight: 600;
    padding: 10px 20px;
    font-size: 15px;
}

.unified-action-btn:hover {
    background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
    box-shadow: 0 2px 6px rgba(59, 130, 246, 0.3);
    transform: translateY(-1px);
}

/* Loading States */
.loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
}

.loading-spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #0076BD;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin-bottom: 20px;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 60px 20px;
    text-align: center;
    color: #dc3545;
}

.error-icon {
    font-size: 48px;
    margin-bottom: 20px;
}

.retry-button {
    padding: 10px 20px;
    background-color: #dc3545;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    margin-top: 15px;
}

.retry-button:hover {
    background-color: #c82333;
}

.empty-results-row {
    background-color: #f8f9fa;
}

.empty-results-cell {
    padding: 60px 20px !important;
    text-align: center;
    border: none;
    vertical-align: middle;
}

.empty-results-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #666;
}

.empty-results-content h3 {
    margin: 0 0 15px 0;
    font-size: 18px;
    color: #333;
}

.empty-results-content p {
    margin: 0 0 15px 0;
    color: #666;
    line-height: 1.5;
    max-width: 400px;
}

.clear-search-button {
    padding: 8px 16px;
    background-color: #6c757d;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    margin-top: 15px;
}

.clear-search-button:hover {
    background-color: #545b62;
}

/* Responsive Design */
@media (max-width: 768px) {
    .pagination-export-row {
        flex-direction: column;
        gap: 12px;
        padding: 12px 16px;
    }
    
    .pagination-section {
        margin-left: 0;
        width: 100%;
        justify-content: center;
    }
    
    .export-section {
        width: 100%;
        justify-content: center;
    }

    .pagination-container {
        flex-direction: column;
        gap: 12px;
        padding: 12px 16px;
    }

    .action-buttons {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
    }

    .controls {
        flex-direction: column;
        gap: 12px;
        align-items: stretch;
        padding: 12px 16px;
    }

    .controls-left {
        flex-direction: column;
        align-items: stretch;
        gap: 12px;
    }

    .controls-right {
        justify-content: space-between;
        flex-wrap: wrap;
        gap: 8px;
    }

    .inline-reach-metrics {
        order: -1;
        justify-content: flex-start;
        flex-wrap: wrap;
    }

    .inline-metric {
        padding: 3px 6px;
    }

    .inline-label, .inline-value {
        font-size: 11px;
    }

    .sets-grid {
        gap: 10px;
    }

    .set-card {
        padding: 12px;
        min-width: 180px;
        max-width: 200px;
    }

    .set-name {
        font-size: 15px;
        margin-bottom: 10px;
    }

    .set-metrics {
        gap: 6px;
    }

    .set-metric {
        padding: 6px;
    }

    .metric-value {
        font-size: 13px;
    }

    .results-table {
        min-width: 2000px;
        font-size: 10px;
    }

    .results-table th,
    .results-table td {
        padding: 6px 3px;
    }

    .section-title {
        font-size: 16px;
        flex-direction: column;
        align-items: flex-start;
        gap: 4px;
    }
}

@media (max-width: 480px) {
    .set-view-container {
        margin: 0 0 16px 0;
        padding: 12px;
    }

    .set-view-header h3 {
        font-size: 15px;
    }

    .sets-grid {
        gap: 8px;
    }

    .set-card {
        padding: 10px;
        min-width: 160px;
        max-width: 180px;
    }

    .set-name {
        font-size: 14px;
        margin-bottom: 8px;
    }

    .set-metrics {
        gap: 4px;
    }

    .set-metric {
        padding: 4px;
    }

    .metric-value {
        font-size: 12px;
    }

    .metric-label {
        font-size: 9px;
    }

    .results-table {
        min-width: 1800px;
        font-size: 9px;
    }

    .results-table th,
    .results-table td {
        padding: 4px 2px;
    }

    .controls {
        padding: 12px;
    }

    .controls-left {
        gap: 8px;
    }

    .filter-label {
        font-size: 13px;
    }

    .toggle-btn {
        padding: 5px 8px;
        font-size: 12px;
    }

    .controls-right {
        gap: 6px;
    }

    .inline-reach-metrics {
        gap: 8px;
    }

    .inline-metric {
        padding: 2px 4px;
    }

    .inline-label, .inline-value {
        font-size: 10px;
    }
}
`}</style>
		</div>
	);
};
